import { sql, relations } from "drizzle-orm";
import { pgTable, text, varchar, integer, decimal, doublePrecision, boolean, timestamp, jsonb, index, uniqueIndex, date } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod/v4";

// Re-export auth models
export * from "./models/auth";
export * from "./models/password-reset";
export * from "./models/subscription-transfers";

// Re-export multi-product platform models (product entitlements)
export * from "./models/products";

// ================== BUILDER PROFILES ==================
export const pdfTemplateDefaults = pgTable("pdf_template_defaults", {
  userId: varchar("user_id").primaryKey(),
  snapshot: jsonb("snapshot").notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});
export const pdfTemplateAssets = pgTable("pdf_template_assets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  objectPath: text("object_path").notNull(),
  kind: text("kind").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, table => [index("pdf_template_assets_user_idx").on(table.userId)]);
export const pdfTemplateGenerations = pgTable("pdf_template_generations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  status: text("status").notNull().default("processing"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
}, table => [index("pdf_template_generations_user_time_idx").on(table.userId, table.createdAt)]);
export const quoteHeadingPreferences = pgTable("quote_heading_preferences", {
  userId: varchar("user_id").primaryKey(),
  names: jsonb("names").$type<Record<string, string>>().notNull().default({}),
});

export const builderProfiles = pgTable("builder_profiles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().unique(),
  companyName: text("company_name").notNull(),
  address: text("address"),
  phone: text("phone"),
  email: text("email"),
  website: text("website"),
  vatNumber: text("vat_number"),
  defaultLabourRate: decimal("default_labour_rate", { precision: 10, scale: 2 }).default("250.00"),
  labourRateType: text("labour_rate_type").default("day"), // 'day' or 'hour'
  defaultMarkupPercent: decimal("default_markup_percent", { precision: 5, scale: 2 }).default("15.00"),
  defaultOverheadPercent: decimal("default_overhead_percent", { precision: 5, scale: 2 }).default("10.00"),
  quoteStyle: text("quote_style").default("detailed"), // 'detailed' or 'simplified'
  logoUrl: text("logo_url"),
  showCompanyName: boolean("show_company_name").notNull().default(true),
  publicQuoteNameAllowed: boolean("public_quote_name_allowed").notNull().default(false),
  logoSize: text("logo_size").notNull().default("medium"), // 'small', 'medium', or 'large'
  brandColor: text("brand_color").default("#1e40af"),
  region: text("region").default("national"), // 'london', 'south_east', 'north_west', 'national', 'custom'
  regionUplift: decimal("region_uplift", { precision: 5, scale: 2 }).default("0.00"),
  quoteValidityDays: integer("quote_validity_days").default(30),
  ownerHourlyRate: decimal("owner_hourly_rate", { precision: 10, scale: 2 }).default("45.00"),
  defaultTermsConditions: text("default_terms_conditions"),
  defaultPaymentTerms: text("default_payment_terms").default("50% deposit upon instruction, 50% upon completion"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertBuilderProfileSchema = createInsertSchema(builderProfiles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertBuilderProfile = z.infer<typeof insertBuilderProfileSchema>;
export type BuilderProfile = typeof builderProfiles.$inferSelect;

// ================== CLIENTS ==================
export const clients = pgTable("clients", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  name: text("name").notNull(),
  email: text("email"),
  phone: text("phone"),
  address: text("address"),
  postcode: text("postcode"),
  notes: text("notes"),
  marginAdjustment: decimal("margin_adjustment", { precision: 5, scale: 2 }).default("0.00"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_clients_user").on(table.userId),
  index("idx_clients_postcode").on(table.postcode),
]);

export const insertClientSchema = createInsertSchema(clients).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertClient = z.infer<typeof insertClientSchema>;
export type Client = typeof clients.$inferSelect;

// ================== COST LIBRARY ==================
export const costItems = pgTable("cost_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id"), // null = system default, userId = user custom
  category: text("category").notNull(), // demolition, steel, plastering, etc.
  name: text("name").notNull(),
  description: text("description"),
  unit: text("unit").notNull(), // 'sqm', 'linear_m', 'each', 'day', 'hour'
  labourRate: decimal("labour_rate", { precision: 10, scale: 2 }).notNull(),
  labourHours: decimal("labour_hours", { precision: 10, scale: 2 }).default("1.00"),
  materialCost: decimal("material_cost", { precision: 10, scale: 2 }).default("0.00"),
  plantCost: decimal("plant_cost", { precision: 10, scale: 2 }).default("0.00"),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_cost_items_user").on(table.userId),
  index("idx_cost_items_category").on(table.category),
]);

export const insertCostItemSchema = createInsertSchema(costItems).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertCostItem = z.infer<typeof insertCostItemSchema>;
export type CostItem = typeof costItems.$inferSelect;

// ================== COST LIBRARY V2 ==================
export const costTasksDefault = pgTable("cost_tasks_default", {
  recipe: jsonb("recipe").$type<unknown>(),
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  market: text("market").default("uk"), // 'uk' | 'au' | 'ca' | 'us'
  category: text("category").notNull(),
  trade: text("trade").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  unit: text("unit").notNull(),
  rateLow: decimal("rate_low", { precision: 10, scale: 2 }).notNull(),
  rateTypical: decimal("rate_typical", { precision: 10, scale: 2 }).notNull(),
  rateHigh: decimal("rate_high", { precision: 10, scale: 2 }).notNull(),
  // Per-task labour/material/plant cost split. NULL means "fall back to category default".
  // Values are fractions of unit cost (e.g. 0.55 / 0.40 / 0.05 for brickwork).
  labourPct: decimal("labour_pct", { precision: 4, scale: 3 }),
  materialPct: decimal("material_pct", { precision: 4, scale: 3 }),
  plantPct: decimal("plant_pct", { precision: 4, scale: 3 }),
  keywords: text("keywords"),
  notesIncluded: text("notes_included"),
  notesExcluded: text("notes_excluded"),
  canonicalKey: text("canonical_key"),
  isActive: boolean("is_active").default(true),
  sortOrder: integer("sort_order").default(0),
  updatedAt: timestamp("updated_at").defaultNow(),
  // isPriceLocked: when true, the AI pipeline must use rateTypical exactly — no
  // rate floors, outlier caps, or AI-price-range logic can deviate from it.
  // Only a user's personal override can beat a locked rate.
  isPriceLocked: boolean("is_price_locked").default(false),
  // isAdminEdited: set automatically whenever an admin changes any rate column
  // via the Master Cost Library UI.  Drives the "Updated" vs "Active" status badge.
  isAdminEdited: boolean("is_admin_edited").default(false),
}, (table) => [
  index("idx_cost_tasks_default_category").on(table.category),
  index("idx_cost_tasks_default_trade").on(table.trade),
  index("idx_cost_tasks_default_canonical").on(table.canonicalKey),
  index("idx_cost_tasks_default_market").on(table.market),
]);

export const insertCostTaskDefaultSchema = createInsertSchema(costTasksDefault).omit({
  recipe: true,
  id: true,
  updatedAt: true,
});
export type InsertCostTaskDefault = z.infer<typeof insertCostTaskDefaultSchema>;
export type CostTaskDefault = typeof costTasksDefault.$inferSelect;

export const recipeDraftRuns = pgTable("recipe_draft_runs", {
  id: text("id").primaryKey().default("uk"),
  baseline: jsonb("baseline").notNull(),
  active: boolean("active").notNull().default(false),
  capturedAt: timestamp("captured_at", { withTimezone: true }).notNull().defaultNow(),
});
export const recipeDrafts = pgTable("recipe_drafts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()::text`),
  taskId: varchar("task_id").notNull().unique().references(() => costTasksDefault.id, { onDelete: "cascade" }),
  market: text("market").notNull().default("uk"),
  taskUnit: text("task_unit").notNull(),
  currentTypical: decimal("current_typical", { precision: 10, scale: 2 }).notNull(),
  draftRecipe: jsonb("draft_recipe"),
  sumDifference: decimal("sum_difference", { precision: 12, scale: 2 }),
  status: text("status").notNull().default("draft"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  note: text("note").notNull().default(""),
  ownerNote: text("owner_note").notNull().default(""),
  generationState: text("generation_state").notNull().default("queued"),
  leaseToken: text("lease_token"),
  leaseUntil: timestamp("lease_until", { withTimezone: true }),
  revision: integer("revision").notNull().default(1),
  attempts: integer("attempts").notNull().default(0),
  retryCount: integer("retry_count").notNull().default(0),
  firstBalanced: boolean("first_balanced"),
  labourFloorAdjusted: boolean("labour_floor_adjusted").notNull().default(false),
}, table => [index("recipe_drafts_queue_idx").on(table.generationState, table.leaseUntil)]);

export const costTasksUserOverrides = pgTable("cost_tasks_user_overrides", {
  recipe: jsonb("recipe").$type<unknown>(),
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  taskId: varchar("task_id").notNull(),
  rateLow: decimal("rate_low", { precision: 10, scale: 2 }),
  rateTypical: decimal("rate_typical", { precision: 10, scale: 2 }),
  rateHigh: decimal("rate_high", { precision: 10, scale: 2 }),
  // Optional per-user override of the labour/material/plant split.
  // NULL means "use the default task split (or category fallback)".
  labourPct: decimal("labour_pct", { precision: 4, scale: 3 }),
  materialPct: decimal("material_pct", { precision: 4, scale: 3 }),
  plantPct: decimal("plant_pct", { precision: 4, scale: 3 }),
  unitOverride: text("unit_override"),
  enabled: boolean("enabled").default(true),
  changeReason: text("change_reason"),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_cost_overrides_user").on(table.userId),
  index("idx_cost_overrides_task").on(table.taskId),
]);

export const insertCostTaskUserOverrideSchema = createInsertSchema(costTasksUserOverrides).omit({
  recipe: true,
  id: true,
  updatedAt: true,
});
export type InsertCostTaskUserOverride = z.infer<typeof insertCostTaskUserOverrideSchema>;
export type CostTaskUserOverride = typeof costTasksUserOverrides.$inferSelect;

export const costTasksUserCustom = pgTable("cost_tasks_user_custom", {
  recipe: jsonb("recipe").$type<unknown>(),
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  category: text("category").notNull(),
  trade: text("trade").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  unit: text("unit").notNull(),
  rateLow: decimal("rate_low", { precision: 10, scale: 2 }).notNull(),
  rateTypical: decimal("rate_typical", { precision: 10, scale: 2 }).notNull(),
  rateHigh: decimal("rate_high", { precision: 10, scale: 2 }).notNull(),
  // Optional labour/material/plant split for the user's own custom task.
  // NULL means "fall back to category default".
  labourPct: decimal("labour_pct", { precision: 4, scale: 3 }),
  materialPct: decimal("material_pct", { precision: 4, scale: 3 }),
  plantPct: decimal("plant_pct", { precision: 4, scale: 3 }),
  keywords: text("keywords"),
  notes: text("notes"),
  canonicalKey: text("canonical_key"),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_cost_custom_user").on(table.userId),
  index("idx_cost_custom_category").on(table.category),
  index("idx_cost_custom_canonical").on(table.canonicalKey),
]);

export const insertCostTaskUserCustomSchema = createInsertSchema(costTasksUserCustom).omit({
  recipe: true,
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertCostTaskUserCustom = z.infer<typeof insertCostTaskUserCustomSchema>;
export type CostTaskUserCustom = typeof costTasksUserCustom.$inferSelect;

export type CostLibraryTask = {
  hasRecipe?: boolean;
  hasRecipeOverride?: boolean;
  hasDefaultRecipe?: boolean;
  id: string;
  source: "default" | "override" | "custom";
  category: string;
  trade: string;
  name: string;
  description: string | null;
  unit: string;
  rateLow: string;
  rateTypical: string;
  rateHigh: string;
  // Effective labour/material/plant split (after applying any user override).
  // NULL means "no task-specific split set — fall back to category default".
  labourPct: string | null;
  materialPct: string | null;
  plantPct: string | null;
  keywords: string | null;
  canonicalKey: string | null;
  defaultRateLow?: string;
  defaultRateTypical?: string;
  defaultRateHigh?: string;
  // Underlying default split, even when an override is applied. Useful for
  // future "reset" functionality on the user-overrides UI.
  defaultLabourPct?: string | null;
  defaultMaterialPct?: string | null;
  defaultPlantPct?: string | null;
  overrideId?: string;
  overrideEnabled?: boolean;
  isActive: boolean | null;
  isHidden?: boolean;
  // Surfaced from the underlying cost_tasks_default row.
  // true  → admin has locked this rate; the AI pipeline must use rateTypical exactly.
  // false / undefined → normal range-based pricing.
  // Always false for user overrides and custom tasks (user rate wins regardless).
  isPriceLocked?: boolean;
  // true → an admin has edited the rate via the Master Cost Library.
  isAdminEdited?: boolean;
};

// ================== COST LIBRARY USER HIDDEN TASKS ==================
export const costTasksUserHidden = pgTable("cost_tasks_user_hidden", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  taskId: varchar("task_id").notNull(),
  taskSource: text("task_source").notNull(),
  hiddenAt: timestamp("hidden_at").defaultNow(),
}, (table) => [
  index("idx_cost_hidden_user").on(table.userId),
  index("idx_cost_hidden_task").on(table.taskId),
]);

export type CostTaskUserHidden = typeof costTasksUserHidden.$inferSelect;

// ================== COST LIBRARY RATE HISTORY ==================
export const costTaskRateHistory = pgTable("cost_task_rate_history", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  taskId: varchar("task_id").notNull(),
  taskSource: text("task_source").notNull(),
  quoteId: varchar("quote_id"),
  oldRateTypical: decimal("old_rate_typical", { precision: 10, scale: 2 }),
  newRateTypical: decimal("new_rate_typical", { precision: 10, scale: 2 }).notNull(),
  oldRateLow: decimal("old_rate_low", { precision: 10, scale: 2 }),
  newRateLow: decimal("new_rate_low", { precision: 10, scale: 2 }),
  oldRateHigh: decimal("old_rate_high", { precision: 10, scale: 2 }),
  newRateHigh: decimal("new_rate_high", { precision: 10, scale: 2 }),
  source: text("source").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_rate_history_user").on(table.userId),
  index("idx_rate_history_task").on(table.taskId),
]);

export const insertCostTaskRateHistorySchema = createInsertSchema(costTaskRateHistory).omit({
  id: true,
  createdAt: true,
});
export type InsertCostTaskRateHistory = z.infer<typeof insertCostTaskRateHistorySchema>;
export type CostTaskRateHistory = typeof costTaskRateHistory.$inferSelect;

// ================== QUOTES ==================
export const quotes = pgTable("quotes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  clientId: varchar("client_id"),
  quoteNumber: text("quote_number").notNull(),
  status: text("status").default("confirmed"), // 'draft', 'confirmed', 'sent', 'accepted', 'rejected'
  projectAddress: text("project_address"),
  projectType: text("project_type"), // 'extension', 'loft', 'wall_opening', etc.
  description: text("description"),
  subtotal: decimal("subtotal", { precision: 12, scale: 2 }).default("0.00"),
  overheadAmount: decimal("overhead_amount", { precision: 12, scale: 2 }).default("0.00"),
  profitAmount: decimal("profit_amount", { precision: 12, scale: 2 }).default("0.00"),
  contingencyAmount: decimal("contingency_amount", { precision: 12, scale: 2 }).default("0.00"),
  totalPrice: decimal("total_price", { precision: 12, scale: 2 }).default("0.00"),
  vatRate: decimal("vat_rate", { precision: 5, scale: 2 }).default("20.00"),
  vatAmount: decimal("vat_amount", { precision: 12, scale: 2 }).default("0.00"),
  grandTotal: decimal("grand_total", { precision: 12, scale: 2 }).default("0.00"),
  includeVat: boolean("include_vat").default(true),
  // When true, the customer-facing PDF and shared view show the per-item
  // labour/material/plant breakdown beneath each line. Builder always sees
  // it on the quote-detail page; this flag only controls customer visibility.
  showCostBreakdown: boolean("show_cost_breakdown").default(false),
  // When true, the builder has confirmed that a structural engineer's design
  // has been reviewed and the structural estimate disclaimer is suppressed
  // on the builder quote view and in the reviewer notes.
  seDesignConfirmed: boolean("se_design_confirmed").default(false),
  // When true, the customer-facing PDF and shared view show the Contingency /
  // Overhead / Profit rows in the Quote Summary. When false, those rows are
  // hidden and the summary jumps straight from Subtotal to Net Total. Builder
  // view always shows the full breakdown. Default true keeps existing PDFs
  // unchanged; new quotes are created with false (hidden by default).
  showMarkupBreakdown: boolean("show_markup_breakdown").default(false).notNull(),
  // When true, the customer-facing PDF and shared view show each line item's
  // long-form description under the title. When false, only the title shows
  // (a "Simple" view). Builder view always shows descriptions.
  showItemDescriptions: boolean("show_item_descriptions").default(true).notNull(),
  // When true, the customer-facing PDF and shared view show the Qty / Unit /
  // Unit Cost columns. When false those columns are hidden and only the
  // Subtotal column remains. Builder view always shows them.
  showQuantityUnits: boolean("show_quantity_units").default(false).notNull(),
  // When true (default), the customer-facing PDF and shared view show
  // per-item pricing (unit cost and subtotal) alongside the itemised list.
  // When false ("Total Price Only"), the full item list and categories are
  // still shown, but unit price / per-item subtotal and the cost breakdown
  // are hidden — only the overall Subtotal/VAT/Grand Total summary shows a
  // price. Toggling this on also switches showQuantityUnits and
  // showCostBreakdown off (cascaded from the UI and enforced server-side).
  // The builder's own internal quote-detail view always shows the full
  // itemised breakdown regardless of this flag. New quotes default this from
  // the builder profile's `quoteStyle` field ("simplified" -> false,
  // "detailed" -> true); can be overridden per quote.
  showLineItems: boolean("show_line_items").default(true).notNull(),
  estimatedDays: integer("estimated_days"),
  assumptions: text("assumptions"),
  exclusions: text("exclusions"),
  termsConditions: text("terms_conditions"),
  paymentTerms: text("payment_terms"),
  projectTimeline: text("project_timeline"), // e.g., "2 WEEKS"
  commencementNote: text("commencement_note"), // e.g., "4-8 Weeks subject to deposit"
  validUntil: timestamp("valid_until"),
  learnedAdjustments: jsonb("learned_adjustments").$type<string[]>().default([]),
  confidenceLevel: text("confidence_level").default("medium"), // 'high', 'medium', 'low'
  headingNames: jsonb("heading_names").$type<Record<string, string>>(),
  pdfTemplate: jsonb("pdf_template"),
  quoteType: text("quote_type").default("auto"), // 'auto', 'template', 'manual'
  // REC-13: 'estimate' = quick pricing (expert reviewer skipped); 'quote' = full pipeline
  quoteClass: text("quote_class").default("quote"), // 'estimate' | 'quote'
  // REC-10: Revision tracking — revisionNumber starts at 1; parentQuoteId links to the original
  revisionNumber: integer("revision_number").default(1),
  parentQuoteId: varchar("parent_quote_id"),
  // Reviewer notes surfaced from the AI pricing pipeline (e.g. "Price flag: ...",
  // waste-filter messages, demolition repricing). Persisted so the saved quote
  // and PDF can show the same review prompts the user saw at generation time.
  reviewerNotes: jsonb("reviewer_notes").$type<string[]>().default([]),
  // Structured validation flags from the AI pipeline (expert reviewer findings:
  // scope gaps, price outliers, quantity concerns, etc.). Persisted as JSONB so
  // the quote-detail page can render the same rich UI shown at generation time.
  validationFlags: jsonb("validation_flags").$type<unknown[]>().default([]),
  // True for quotes generated by the automated regression / monitoring batch.
  // Hidden from the normal Quotes list; only the admin can opt-in to view them.
  isTest: boolean("is_test").default(false).notNull(),
  // Original filenames uploaded by the user during AI generation (e.g. ["drawings.pdf", "photo.jpg"]).
  // Empty array means no files were uploaded (description-only quote).
  uploadedFileNames: jsonb("uploaded_file_names").$type<string[]>().default([]),
  // Object storage keys for the uploaded files, parallel to uploadedFileNames.
  uploadedFileKeys: jsonb("uploaded_file_keys").$type<string[]>().default([]),
  // Snapshot of AI-generated line items captured at quote creation (auto quotes only).
  // Never overwritten — used to diff against the final saved line items to show
  // what the builder changed during the review step before saving.
  originalLineItemsSnapshot: jsonb("original_line_items_snapshot").$type<unknown[]>(),
  // Multi-scope quotes (Task #680): true when this quote was generated from a
  // job spanning several distinct projects (extension + loft + refurb, etc.).
  // Drives the scope tabs + Combined tab in the review/detail UI. Default false
  // so ordinary single-scope quotes behave exactly as before.
  isMultiScope: boolean("is_multi_scope").default(false).notNull(),
  // Ordered list of detected scope names for a multi-scope quote (e.g.
  // ["Rear Extension", "Loft Conversion"]). Empty on single-scope quotes.
  scopeNames: jsonb("scope_names").$type<string[]>().default([]),
  // When true, the customer-facing view and PDF collapse the full item list
  // into one row per category, each showing only the category name and its
  // combined subtotal.  The full itemised breakdown is still stored and visible
  // to the builder; the customer sees a concise summary.  Default false keeps
  // all existing quotes showing the full itemised view.
  summaryMode: boolean("summary_mode").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_quotes_user").on(table.userId),
  index("idx_quotes_client").on(table.clientId),
  index("idx_quotes_status").on(table.status),
  index("idx_quotes_is_test").on(table.isTest),
]);

export const insertQuoteSchema = createInsertSchema(quotes).omit({
  id: true,
  headingNames: true,
  pdfTemplate: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertQuote = z.infer<typeof insertQuoteSchema>;
export type Quote = typeof quotes.$inferSelect;

// ================== QUOTE LINE ITEMS ==================
export const quoteLineItems = pgTable("quote_line_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  quoteId: varchar("quote_id").notNull(),
  section: text("section").default("MAIN WORKS"), // SITE WORK, SUBSTRUCTURE, STRUCTURE, SERVICES, FINISHES, OTHER
  // Multi-scope quotes (Task #680): which detected scope this line belongs to
  // (e.g. "Rear Extension", "Loft Conversion"). Shared site-cost lines carry
  // the sentinel "__shared__". NULL on ordinary single-scope quotes.
  scopeName: text("scope_name"),
  category: text("category").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  // Simplified, plain-English rendering of `description` for customer-facing
  // surfaces (customer PDF, shared view). `description` itself is the
  // technical/internal field the AI writes and the quote-generation pipeline's
  // matching logic (beam renaming, size-based dedup, description-scope
  // verification) parses — it deliberately keeps labels/section sizes/specs.
  // customerDescription is derived deterministically after all scope
  // reconciliation completes (see deriveCustomerDescription in
  // post-processing-rules.ts) and never feeds back into matching logic.
  customerDescription: text("customer_description"),
  // Optional customer-facing grouping labels supplied by the quote builder.
  // These are presentation metadata only; category/trade/subcategory remain
  // the canonical classification fields.
  displayStage: text("display_stage"),
  displaySubGroup: text("display_sub_group"),
  inclusions: jsonb("inclusions").$type<string[]>().default([]), // Items included with checkmark
  exclusions: jsonb("exclusions").$type<string[]>().default([]), // Items excluded with X mark
  quantity: decimal("quantity", { precision: 10, scale: 2 }).notNull(),
  unit: text("unit").notNull(),
  unitCost: decimal("unit_cost", { precision: 10, scale: 2 }).notNull(),
  labourHours: decimal("labour_hours", { precision: 10, scale: 2 }).default("0.00"),
  labourCost: decimal("labour_cost", { precision: 10, scale: 2 }).default("0.00"),
  materialCost: decimal("material_cost", { precision: 10, scale: 2 }).default("0.00"),
  plantCost: decimal("plant_cost", { precision: 10, scale: 2 }).default("0.00"),
  // Quote-local copy of the item's detailed recipe; never writes back to the Cost Library.
  quoteCostRecipe: jsonb("quote_cost_recipe").$type<unknown>(),
  // Builder-approved purchase materials, independent of the selling-price recipe.
  materialAllowanceApproval: jsonb("material_allowance_approval").$type<unknown>(),
  // Builder-only geometry provenance; null for every legacy quote line.
  quantityDerivation: jsonb("quantity_derivation").$type<unknown>(),
  subtotal: decimal("subtotal", { precision: 12, scale: 2 }).notNull(),
  // Quote-local provenance only; never writes back to a library rate.
  priceSource: text("price_source"),
  isOptional: boolean("is_optional").default(false), // For optional add-on items
  sortOrder: integer("sort_order").default(0),
  // Per-line price-verification flag from the AI pipeline (named caps + outlier detector).
  // When present, the line should render a "review this price" indicator on quote-detail and PDF.
  priceVerificationFlag: jsonb("price_verification_flag").$type<{
    itemName: string;
    category: string;
    aiPrice: number;
    costLibraryHigh: number;
    costLibraryTypical: number;
    deviationPercent: number;
    correctedPrice: number;
    reason: string;
  } | null>().default(null),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_line_items_quote").on(table.quoteId),
]);

export const insertQuoteLineItemSchema = createInsertSchema(quoteLineItems).omit({
  id: true,
  createdAt: true,
});

export type InsertQuoteLineItem = z.infer<typeof insertQuoteLineItemSchema>;
export type QuoteLineItem = typeof quoteLineItems.$inferSelect;

// ================== QUICKBOOKS INTEGRATION ==================
export const quickbooksConnections = pgTable("quickbooks_connections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().unique(),
  realmId: text("realm_id").notNull(),
  companyName: text("company_name"),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  accessTokenExpiresAt: timestamp("access_token_expires_at"),
  refreshTokenExpiresAt: timestamp("refresh_token_expires_at"),
  needsReconnect: boolean("needs_reconnect").notNull().default(false),
  lastError: text("last_error"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [index("idx_quickbooks_connections_user").on(table.userId)]);
export type QuickbooksConnection = typeof quickbooksConnections.$inferSelect;

export const quickbooksOauthStates = pgTable("quickbooks_oauth_states", {
  state: varchar("state").primaryKey(),
  userId: varchar("user_id").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [index("idx_quickbooks_oauth_states_user").on(table.userId)]);

export const quickbooksInvoices = pgTable("quickbooks_invoices", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  quoteId: varchar("quote_id").notNull().unique(),
  realmId: text("realm_id").notNull(),
  quickbooksInvoiceId: text("quickbooks_invoice_id"),
  docNumber: text("doc_number"),
  status: text("status").notNull().default("in_progress"),
  invoiceUrl: text("invoice_url"),
  lastError: text("last_error"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_quickbooks_invoices_user").on(table.userId),
  index("idx_quickbooks_invoices_quote").on(table.quoteId),
]);
export type QuickbooksInvoice = typeof quickbooksInvoices.$inferSelect;

// ================== SITE SURVEYS ==================
// A "Site Survey" is the 4th capture mode (alongside Auto/Template/Manual).
// The builder walks a property capturing voice notes (auto-transcribed),
// photos, typed notes and work items per room. The output is a Site Visit
// Report PDF that can be uploaded into Auto Quote to drive the quote.
//
// `data` holds the flexible nested shape (rooms, items, transcripts, photo
// objectPaths) so we don't have to schema every field.
export const siteSurveys = pgTable("site_surveys", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  // Optional links — survey can exist before any client/quote is created.
  clientId: varchar("client_id"),
  quoteId: varchar("quote_id"), // populated once the survey is sent to Auto Quote
  // Display title used on the dashboard list (e.g. project address or type).
  title: text("title").notNull().default("Untitled site visit"),
  status: text("status").notNull().default("draft"), // 'draft' | 'completed'
  // Top-level project metadata for quick filtering / dashboard display.
  projectAddress: text("project_address"),
  projectType: text("project_type"),
  // Optional pointer to the generated PDF in object storage so it can be
  // re-downloaded without regeneration.
  reportObjectPath: text("report_object_path"),
  data: jsonb("data").$type<{
    project?: {
      clientName?: string;
      projectAddress?: string;
      projectType?: string;
      description?: string;
      capturedBy?: string;
      visitDate?: string; // ISO
    };
    rooms?: Array<{
      id: string;
      name: string; // e.g. "Kitchen", "Bedroom 2"
      templateId?: string;
      voiceNotes?: Array<{
        id: string;
        transcript: string;
        audioObjectPath?: string; // optional original audio in storage
        durationSec?: number;
        createdAt: string; // ISO
      }>;
      typedNotes?: string;
      workItems?: string[];
      photos?: Array<{
        id: string;
        objectPath: string;
        caption?: string;
        createdAt: string; // ISO
      }>;
    }>;
    generalNotes?: string;
  }>().default({}),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_site_surveys_user").on(table.userId),
  index("idx_site_surveys_status").on(table.status),
]);

export const insertSiteSurveySchema = createInsertSchema(siteSurveys).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertSiteSurvey = z.infer<typeof insertSiteSurveySchema>;
export type SiteSurvey = typeof siteSurveys.$inferSelect;

// ================== ENGINEER STRUCTURAL REPORTS ==================
// First engineer-product feature: a structural report writer. An engineer
// creates a report, uploads site photos + typed notes + transcribed voice
// notes, AI drafts the fixed sections below, they edit/finalise in-app and
// export a professional PDF. Engineer-only data lives in its OWN table
// (never reuse builder tables) and is gated behind the 'engineer' product.
//
// The report body is a FIXED set of sections, always in this order:
export const REPORT_SECTION_KEYS = [
  "introduction",
  "scope",
  "observations",
  "findings",
  "recommendations",
] as const;
export type ReportSectionKey = (typeof REPORT_SECTION_KEYS)[number];

export type ReportSections = Partial<Record<ReportSectionKey, string>>;

export const engineerReports = pgTable("engineer_reports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  // Display title shown on the dashboard list (defaults until the engineer
  // sets a project address / title).
  title: text("title").notNull().default("Untitled report"),
  status: text("status").notNull().default("draft"), // 'draft' | 'finalised'
  // Top-level report metadata used on the cover page and dashboard.
  clientName: text("client_name"),
  projectAddress: text("project_address"),
  reference: text("reference"), // engineer's own report reference number
  inspectionDate: text("inspection_date"), // ISO date string
  engineerName: text("engineer_name"),
  engineerTitle: text("engineer_title"),
  // Optional pointer to the exported PDF in object storage.
  reportObjectPath: text("report_object_path"),
  // Flexible jsonb payload mirroring the site-survey pattern: captured inputs
  // (typed notes, transcribed voice notes, photos) + the generated/edited
  // report sections + a lightweight version history of section snapshots.
  data: jsonb("data").$type<{
    notes?: string;
    voiceNotes?: Array<{
      id: string;
      transcript: string;
      durationSec?: number;
      createdAt: string; // ISO
    }>;
    photos?: Array<{
      id: string;
      objectPath: string;
      caption?: string;
      createdAt: string; // ISO
    }>;
    sections?: ReportSections;
    generatedAt?: string; // ISO — when the AI last drafted the sections
    versions?: Array<{
      id: string;
      createdAt: string; // ISO
      label?: string;
      sections: ReportSections;
    }>;
  }>().default({}),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_engineer_reports_user").on(table.userId),
  index("idx_engineer_reports_status").on(table.status),
]);

export const insertEngineerReportSchema = createInsertSchema(engineerReports).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertEngineerReport = z.infer<typeof insertEngineerReportSchema>;
export type EngineerReport = typeof engineerReports.$inferSelect;

// ================== ENGINEER REPORT CHECKS ==================
// Second engineer-product feature: a structural report CHECKER. The engineer
// uploads an EXISTING structural report PDF and the AI reviews it against a
// curated structural / Building-Regs checklist, returning structured findings
// (missing information, inconsistencies, unclear wording, structural concerns,
// Building-Reg flags) each carrying a severity. Findings are persisted on the
// row (cached-audit pattern) so a check can be revisited without re-running
// the AI. Engineer-only data lives in its OWN table (never reuse builder or
// report-writer tables) and is gated behind the 'engineer' product.
//
// The finding categories are a FIXED, curated set (not a trained model):
export const REPORT_CHECK_CATEGORY_KEYS = [
  "missing_information",
  "inconsistency",
  "unclear_wording",
  "structural_concern",
  "building_regulations",
] as const;
export type ReportCheckCategoryKey = (typeof REPORT_CHECK_CATEGORY_KEYS)[number];

// Severity of a single finding.
export const REPORT_CHECK_SEVERITY_KEYS = ["high", "medium", "low"] as const;
export type ReportCheckSeverityKey = (typeof REPORT_CHECK_SEVERITY_KEYS)[number];

export interface ReportCheckFinding {
  id: string;
  category: ReportCheckCategoryKey;
  severity: ReportCheckSeverityKey;
  title: string;
  detail: string;
  recommendation?: string;
  // Optional pointer to where in the report the issue was found (section
  // heading, page, or quoted phrase) so the engineer can locate it quickly.
  location?: string;
}

export const engineerReportChecks = pgTable("engineer_report_checks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  // Display title shown on the checks list (defaults to the uploaded filename).
  title: text("title").notNull().default("Untitled check"),
  // 'pending'  — uploaded, not yet reviewed
  // 'checking' — AI review in progress (best-effort marker)
  // 'checked'  — findings available
  status: text("status").notNull().default("pending"),
  // The uploaded source report PDF (object storage pointer + original name).
  sourceObjectPath: text("source_object_path"),
  sourceFileName: text("source_file_name"),
  // Flexible jsonb payload: extraction metadata + the AI findings + a cached
  // summary + when the check last ran (cached-audit pattern — mirrors the
  // generated_quote_logs.audit_result design so a check is never re-run on
  // every view).
  data: jsonb("data").$type<{
    pageCount?: number;
    extractedChars?: number;
    summary?: string;
    findings?: ReportCheckFinding[];
    checkedAt?: string; // ISO — when the AI last reviewed the report
    model?: string;
    checklistVersion?: number;
    verdict?: "pass" | "flag" | "fail";
    verdictReasons?: Array<{ code: string; findingId?: string; message: string }>;
    agreement?: { annotatedChecks: number; accepted: number; rejected: number; agreementPercent?: number };
  }>().default({}),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_engineer_report_checks_user").on(table.userId),
  index("idx_engineer_report_checks_status").on(table.status),
]);

export const insertEngineerReportCheckSchema = createInsertSchema(engineerReportChecks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertEngineerReportCheck = z.infer<typeof insertEngineerReportCheckSchema>;
export type EngineerReportCheck = typeof engineerReportChecks.$inferSelect;

// ================== ENGINEER REPORT CHECK AUDIT ==================
// Runs and findings are deliberately separate from the legacy JSON cache on
// engineer_report_checks. A rerun is a new, immutable cohort; it must never
// overwrite a reviewer disposition made against an earlier output.
export const engineerReportChecklists = pgTable("engineer_report_checklists", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  version: integer("version").notNull(),
  items: jsonb("items").$type<Array<{
    id: string;
    title: string;
    category: "missing_information" | "inconsistency" | "unclear_wording" | "structural_concern" | "building_regulations";
    instruction: string;
    severityRule: {
      allowed: ReportCheckSeverityKey[];
      allowSafetyCritical: boolean;
      guidance: string;
    };
    status: "active" | "retired";
  }>>().notNull(),
  createdByUserId: varchar("created_by_user_id"),
  publishedAt: timestamp("published_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [index("idx_engineer_checklists_version").on(table.version)]);
export type EngineerReportChecklist = typeof engineerReportChecklists.$inferSelect;

export const engineerReportCheckRuns = pgTable("engineer_report_check_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  checkId: varchar("check_id").notNull(),
  checklistVersion: integer("checklist_version").notNull(),
  checklistSnapshot: jsonb("checklist_snapshot").$type<Array<{
    id: string;
    title: string;
    category: "missing_information" | "inconsistency" | "unclear_wording" | "structural_concern" | "building_regulations";
    instruction: string;
    severityRule: {
      allowed: ReportCheckSeverityKey[];
      allowSafetyCritical: boolean;
      guidance: string;
    };
    status: "active" | "retired";
  }>>().notNull(),
  summary: text("summary").notNull(),
  model: text("model").notNull(),
  settings: jsonb("settings").$type<Record<string, unknown>>().default({}),
  // A deterministic identity for the model, immutable checklist and settings
  // used for this run. It permits comparable checker-only cohorts without
  // leaking report/source identifiers.
  cohortKey: text("cohort_key").notNull().default("legacy"),
  pageCount: integer("page_count"),
  extractedChars: integer("extracted_chars"),
  verdict: text("verdict").notNull(), // pass | flag | fail; advisory only
  verdictReasons: jsonb("verdict_reasons").$type<Array<{ code: string; findingId?: string; message: string }>>().default([]),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_engineer_check_runs_check").on(table.checkId),
  index("idx_engineer_check_runs_cohort").on(table.cohortKey),
]);
export type EngineerReportCheckRun = typeof engineerReportCheckRuns.$inferSelect;

export const engineerReportCheckFindings = pgTable("engineer_report_check_findings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull(),
  sequence: integer("sequence").notNull(),
  checklistCode: text("checklist_code").notNull(),
  category: text("category").notNull(),
  severity: text("severity").notNull(),
  safetyCritical: boolean("safety_critical").notNull().default(false),
  title: text("title").notNull(),
  detail: text("detail").notNull().default(""),
  recommendation: text("recommendation"),
  location: text("location"),
  disposition: text("disposition").notNull().default("none"), // accept | reject | none
  rejectionReason: text("rejection_reason"), // wrong | true_but_minor | duplicate
  dispositionNote: text("disposition_note"),
  dispositionByUserId: varchar("disposition_by_user_id"),
  dispositionAt: timestamp("disposition_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_engineer_check_findings_run").on(table.runId),
  index("idx_engineer_check_findings_disposition").on(table.disposition),
]);
export type EngineerReportCheckFindingRecord = typeof engineerReportCheckFindings.$inferSelect;

export const engineerReportCheckFindingDispositionAudit = pgTable("engineer_report_check_finding_disposition_audit", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  findingId: varchar("finding_id").notNull(),
  disposition: text("disposition").notNull(),
  rejectionReason: text("rejection_reason"),
  note: text("note"),
  actorUserId: varchar("actor_user_id").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [index("idx_engineer_check_disposition_audit_finding").on(table.findingId)]);

// Pseudonymous checker summaries are intentionally independent from the
// optional internal development-record module. Any source association is held
// separately and is never returned by normal checker endpoints.
export const engineerReportCheckRunSummaries = pgTable("engineer_report_check_run_summaries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull(),
  pseudonym: varchar("pseudonym").notNull(),
  // One overall reviewer annotation for a run: the output broadly agreed with
  // the reviewer, or it missed an issue. This is deliberately checker-native,
  // not an internal R&D record.
  annotation: text("annotation"), // agree | missed
  annotatedByUserId: varchar("annotated_by_user_id"),
  annotatedAt: timestamp("annotated_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [index("idx_engineer_check_summaries_run").on(table.runId)]);
export type EngineerReportCheckRunSummary = typeof engineerReportCheckRunSummaries.$inferSelect;

export const engineerReportCheckSourceLinks = pgTable("engineer_report_check_source_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull(),
  checkId: varchar("check_id").notNull(),
  sourceObjectPath: text("source_object_path").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [index("idx_engineer_check_source_links_run").on(table.runId)]);

export const engineerReportCheckSignoffs = pgTable("engineer_report_check_signoffs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull(),
  signedOffByUserId: varchar("signed_off_by_user_id").notNull(),
  blockingReasons: jsonb("blocking_reasons").$type<Array<{ findingId: string; reason: string }>>().notNull().default([]),
  note: text("note"),
  activeReviewMs: integer("active_review_ms"),
  preparationMinutes: integer("preparation_minutes"),
  signedOffAt: timestamp("signed_off_at").defaultNow(),
}, (table) => [index("idx_engineer_check_signoffs_run").on(table.runId)]);
export type EngineerReportCheckSignoff = typeof engineerReportCheckSignoffs.$inferSelect;

export const privateObjectUploadClaims = pgTable("private_object_upload_claims", {
  objectPath: text("object_path").primaryKey(),
  ownerUserId: varchar("owner_user_id").notNull(),
  // Optional modules use a neutral scope marker so access remains restrictive
  // even when their routes are not composed in this process.
  accessScope: text("access_scope"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [index("idx_private_object_claim_owner").on(table.ownerUserId)]);

// ================== MOBILE AUTH TOKENS ==================
// Opaque bearer tokens issued to the native mobile companion app. The web
// apps use session cookies, but the Expo bundle runs outside the browser and
// cannot carry an httpOnly cookie, so it authenticates with a long-lived
// bearer token stored in the device keychain (expo-secure-store). Each token
// maps to a platform user; `isAuthenticated` accepts it as a fallback when no
// session cookie is present.
export const mobileAuthTokens = pgTable("mobile_auth_tokens", {
  token: varchar("token").primaryKey(),
  userId: varchar("user_id").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  lastUsedAt: timestamp("last_used_at").defaultNow(),
  expiresAt: timestamp("expires_at").notNull(),
}, (table) => [
  index("idx_mobile_auth_tokens_user").on(table.userId),
]);

export const insertMobileAuthTokenSchema = createInsertSchema(mobileAuthTokens).omit({
  createdAt: true,
  lastUsedAt: true,
});

export type InsertMobileAuthToken = z.infer<typeof insertMobileAuthTokenSchema>;
export type MobileAuthToken = typeof mobileAuthTokens.$inferSelect;

// ================== LEARNED ADJUSTMENTS ==================
export const learnedAdjustments = pgTable("learned_adjustments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  category: text("category").notNull(), // 'plastering', 'steel', etc.
  adjustmentType: text("adjustment_type").notNull(), // 'multiplier' or 'additive'
  adjustmentValue: text("adjustment_value").notNull(),
  reason: text("reason"), // 'labour always higher', 'waste underestimated', etc.
  triggerProjectType: text("trigger_project_type"), // optional filter
  triggerClientId: varchar("trigger_client_id"), // optional filter for client-specific
  triggerRegion: text("trigger_region"), // optional filter
  confidenceScore: integer("confidence_score").default(1), // increases with repeats
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_adjustments_user").on(table.userId),
  index("idx_adjustments_category").on(table.category),
]);

export const insertLearnedAdjustmentSchema = createInsertSchema(learnedAdjustments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertLearnedAdjustment = z.infer<typeof insertLearnedAdjustmentSchema>;
export type LearnedAdjustment = typeof learnedAdjustments.$inferSelect;

// ================== PRICE GUIDES (User-learned prices) ==================
export const priceGuides = pgTable("price_guides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  signatureKey: text("signature_key").notNull(),
  category: text("category").notNull(),
  itemName: text("item_name").notNull(),
  unit: text("unit"),
  oldPrice: decimal("old_price", { precision: 10, scale: 2 }),
  newPrice: decimal("new_price", { precision: 10, scale: 2 }).notNull(),
  note: text("note"),
  usageCount: integer("usage_count").default(0),
  lastUsedAt: timestamp("last_used_at"),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_price_guides_user").on(table.userId),
  index("idx_price_guides_category").on(table.category),
  index("idx_price_guides_signature").on(table.userId, table.signatureKey),
]);

export const insertPriceGuideSchema = createInsertSchema(priceGuides).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertPriceGuide = z.infer<typeof insertPriceGuideSchema>;
export type PriceGuide = typeof priceGuides.$inferSelect;

// ================== ANALYSIS PROFILES ==================
export const analysisProfiles = pgTable("analysis_profiles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  name: varchar("name", { length: 100 }).notNull(),
  description: text("description"),
  isActive: boolean("is_active").default(false),
  configPayload: jsonb("config_payload").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_analysis_profiles_user").on(table.userId),
  index("idx_analysis_profiles_active").on(table.userId, table.isActive),
]);

export const insertAnalysisProfileSchema = createInsertSchema(analysisProfiles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertAnalysisProfile = z.infer<typeof insertAnalysisProfileSchema>;
export type AnalysisProfile = typeof analysisProfiles.$inferSelect;

// ================== QUOTE IMPORTS (Historical Quote Upload) ==================
export const userQuoteImports = pgTable("user_quote_imports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  sourceType: text("source_type").notNull(), // pdf, csv, xlsx, json, text
  filename: text("filename"),
  parsingStatus: text("parsing_status").default("pending"), // pending, processing, completed, failed, needs_review
  parsingConfidence: decimal("parsing_confidence", { precision: 5, scale: 2 }),
  itemCount: integer("item_count").default(0),
  totalValue: decimal("total_value", { precision: 12, scale: 2 }),
  notes: text("notes"),
  rawContent: text("raw_content"),
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_quote_imports_user").on(table.userId),
]);

export const insertUserQuoteImportSchema = createInsertSchema(userQuoteImports).omit({
  id: true,
  createdAt: true,
});
export type InsertUserQuoteImport = z.infer<typeof insertUserQuoteImportSchema>;
export type UserQuoteImport = typeof userQuoteImports.$inferSelect;

// ================== NORMALIZED QUOTE ITEMS ==================
export const userQuoteItemsNormalized = pgTable("user_quote_items_normalized", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  importId: varchar("import_id").notNull(),
  userId: varchar("user_id").notNull(),
  rawItemName: text("raw_item_name").notNull(),
  normalizedItemName: text("normalized_item_name"),
  canonicalKey: text("canonical_key"),
  category: text("category"),
  unitType: text("unit_type"),
  quantity: decimal("quantity", { precision: 10, scale: 2 }),
  unitRate: decimal("unit_rate", { precision: 10, scale: 2 }),
  lineTotal: decimal("line_total", { precision: 12, scale: 2 }),
  vatRate: decimal("vat_rate", { precision: 5, scale: 2 }),
  metadataJson: jsonb("metadata_json").$type<Record<string, any>>(),
  confidence: text("confidence").default("medium"), // high, medium, low
  matchType: text("match_type"), // exact, fuzzy, manual, unmatched
}, (table) => [
  index("idx_normalized_items_import").on(table.importId),
  index("idx_normalized_items_user").on(table.userId),
  index("idx_normalized_items_canonical").on(table.canonicalKey),
]);

export const insertUserQuoteItemNormalizedSchema = createInsertSchema(userQuoteItemsNormalized).omit({
  id: true,
});
export type InsertUserQuoteItemNormalized = z.infer<typeof insertUserQuoteItemNormalizedSchema>;
export type UserQuoteItemNormalized = typeof userQuoteItemsNormalized.$inferSelect;

// ================== PRICE SIGNALS (Learned from historical data) ==================
export const userPriceSignals = pgTable("user_price_signals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  canonicalKey: text("canonical_key").notNull(),
  category: text("category").notNull(),
  unitType: text("unit_type").notNull(),
  typicalUnitRateMin: decimal("typical_unit_rate_min", { precision: 10, scale: 2 }),
  typicalUnitRateMedian: decimal("typical_unit_rate_median", { precision: 10, scale: 2 }),
  typicalUnitRateMax: decimal("typical_unit_rate_max", { precision: 10, scale: 2 }),
  varianceScore: decimal("variance_score", { precision: 5, scale: 2 }),
  sampleCount: integer("sample_count").default(0),
  confidence: text("confidence").default("low"), // high (5+), medium (3-4), low (1-2)
  lastUpdated: timestamp("last_updated").defaultNow(),
}, (table) => [
  index("idx_price_signals_user").on(table.userId),
  index("idx_price_signals_canonical").on(table.userId, table.canonicalKey),
]);

export const insertUserPriceSignalSchema = createInsertSchema(userPriceSignals).omit({
  id: true,
  lastUpdated: true,
});
export type InsertUserPriceSignal = z.infer<typeof insertUserPriceSignalSchema>;
export type UserPriceSignal = typeof userPriceSignals.$inferSelect;

// ================== PRICING RULES (User-defined custom rules) ==================
export const userPricingRules = pgTable("user_pricing_rules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  name: text("name").notNull(),
  scopeFilterJson: jsonb("scope_filter_json").$type<{
    categories?: string[];
    canonicalKeys?: string[];
    projectTypes?: string[];
    regions?: string[];
  }>(),
  ruleType: text("rule_type").notNull(), // multiplier, fixed_rate, min_price, margin_override, rounding
  rulePayloadJson: jsonb("rule_payload_json").$type<{
    value?: number;
    roundTo?: number;
    minPrice?: number;
    maxPrice?: number;
  }>().notNull(),
  priority: integer("priority").default(0),
  enabled: boolean("enabled").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_pricing_rules_user").on(table.userId),
]);

export const insertUserPricingRuleSchema = createInsertSchema(userPricingRules).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertUserPricingRule = z.infer<typeof insertUserPricingRuleSchema>;
export type UserPricingRule = typeof userPricingRules.$inferSelect;

// ================== RELATIONS ==================
export const builderProfilesRelations = relations(builderProfiles, ({ one }) => ({
  // No direct relations needed
}));

export const clientsRelations = relations(clients, ({ many }) => ({
  quotes: many(quotes),
}));

export const quotesRelations = relations(quotes, ({ one, many }) => ({
  client: one(clients, {
    fields: [quotes.clientId],
    references: [clients.id],
  }),
  lineItems: many(quoteLineItems),
}));

export const quoteLineItemsRelations = relations(quoteLineItems, ({ one }) => ({
  quote: one(quotes, {
    fields: [quoteLineItems.quoteId],
    references: [quotes.id],
  }),
}));

// ================== CONSTANTS ==================
export const PROJECT_TYPES = [
  { value: "extension", label: "Extension" },
  { value: "loft_conversion", label: "Loft Conversion" },
  { value: "wall_opening", label: "Wall Opening / RSJ" },
  { value: "garage_conversion", label: "Garage Conversion" },
  { value: "kitchen_refurb", label: "Kitchen Renovation" },
  { value: "bathroom_refurb", label: "Bathroom Renovation" },
  { value: "house_refurb", label: "House Renovation" },
  { value: "chimney_removal", label: "Chimney Removal" },
  { value: "full_refurb", label: "Full Refurbishment" },
  { value: "new_build", label: "New Build" },
  { value: "roofing", label: "Roofing" },
  { value: "landscaping", label: "Landscaping" },
  { value: "electrics", label: "Electrical Work" },
  { value: "plumbing", label: "Plumbing Work" },
  { value: "heating", label: "Heating / Boiler" },
  { value: "windows_doors", label: "Windows & Doors" },
  { value: "flooring", label: "Flooring" },
  { value: "decorating", label: "Decorating" },
  { value: "driveway", label: "Driveway / Paving" },
  { value: "fencing", label: "Fencing / Gates" },
  { value: "other", label: "Other" },
] as const;

export const COST_CATEGORIES = [
  { value: "demolition", label: "Demolition & Strip-out" },
  { value: "groundworks", label: "Groundworks & Drainage" },
  { value: "foundations", label: "Foundations & Concrete" },
  { value: "brickwork", label: "Brickwork & Blockwork" },
  { value: "carpentry", label: "Carpentry & Joinery" },
  { value: "roofing", label: "Roofing" },
  { value: "windows_doors", label: "Windows & External Doors" },
  { value: "internal_walls", label: "Internal Walls & Partitions" },
  { value: "plastering", label: "Plastering & Drylining" },
  { value: "insulation", label: "Insulation & Airtightness" },
  { value: "damp_proofing", label: "Waterproofing" },
  { value: "electrics", label: "Electrical" },
  { value: "plumbing", label: "Plumbing & Heating" },
  { value: "ventilation", label: "Ventilation" },
  { value: "flooring", label: "Flooring" },
  { value: "tiling", label: "Tiling" },
  { value: "decorating", label: "Decorating" },
  { value: "kitchens", label: "Kitchen Fitting" },
  { value: "bathrooms", label: "Bathroom Fitting" },
  { value: "steel", label: "Steelwork & Structural" },
  { value: "scaffolding", label: "Scaffolding & Access" },
  { value: "waste", label: "Waste Removal & Skips" },
  { value: "making_good", label: "Making Good & Snagging" },
  { value: "preliminaries", label: "Preliminaries" },
  { value: "other", label: "Other" },
] as const;

export const COST_TRADES: Record<string, { label: string; trades: { value: string; label: string }[] }> = {
  demolition: { label: "Demolition & Strip-out", trades: [
    { value: "strip_out", label: "Strip-out" },
    { value: "structural_demolition", label: "Structural Demolition" },
  ]},
  groundworks: { label: "Groundworks & Drainage", trades: [
    { value: "excavation", label: "Excavation" },
    { value: "drainage", label: "Drainage" },
  ]},
  foundations: { label: "Foundations & Concrete", trades: [
    { value: "foundations", label: "Foundations" },
    { value: "concrete", label: "Concrete Work" },
    { value: "wall_formwork", label: "Wall Formwork" },
  ]},
  brickwork: { label: "Brickwork & Blockwork", trades: [
    { value: "brickwork", label: "Brickwork" },
    { value: "blockwork", label: "Blockwork" },
  ]},
  carpentry: { label: "Carpentry & Joinery", trades: [
    { value: "first_fix_carpentry", label: "1st Fix Carpentry" },
    { value: "second_fix_carpentry", label: "2nd Fix Carpentry" },
    { value: "roof_carpentry", label: "Roof Carpentry" },
  ]},
  roofing: { label: "Roofing", trades: [
    { value: "roof_structure", label: "Roof Structure" },
    { value: "roof_coverings", label: "Roof Coverings" },
    { value: "leadwork", label: "Leadwork" },
    { value: "gutters_downpipes", label: "Gutters & Downpipes" },
  ]},
  windows_doors: { label: "Windows & External Doors", trades: [
    { value: "windows", label: "Windows" },
    { value: "external_doors", label: "External Doors" },
  ]},
  internal_walls: { label: "Internal Walls & Partitions", trades: [
    { value: "stud_walls", label: "Stud Walls" },
    { value: "block_partitions", label: "Block Partitions" },
    { value: "plasterboard", label: "Plasterboard" },
  ]},
  plastering: { label: "Plastering & Drylining", trades: [
    { value: "plastering_wet", label: "Wet Plastering" },
    { value: "drylining", label: "Drylining" },
  ]},
  insulation: { label: "Insulation & Airtightness", trades: [
    { value: "insulation", label: "Insulation" },
  ]},
  damp_proofing: { label: "Waterproofing", trades: [
    { value: "damp_proofing", label: "Damp Proofing" },
    { value: "tanking", label: "Tanking & Waterproofing" },
  ]},
  electrics: { label: "Electrical", trades: [
    { value: "first_fix_electrics", label: "1st Fix Electrics" },
    { value: "second_fix_electrics", label: "2nd Fix Electrics" },
    { value: "testing_certification", label: "Testing & Certification" },
  ]},
  plumbing: { label: "Plumbing & Heating", trades: [
    { value: "first_fix_plumbing", label: "1st Fix Plumbing" },
    { value: "second_fix_plumbing", label: "2nd Fix Plumbing" },
    { value: "heating", label: "Heating" },
    { value: "underfloor_heating", label: "Underfloor Heating" },
  ]},
  ventilation: { label: "Ventilation", trades: [
    { value: "ventilation", label: "Ventilation" },
  ]},
  flooring: { label: "Flooring", trades: [
    { value: "screeding", label: "Screeding" },
    { value: "floor_finishes", label: "Floor Finishes" },
  ]},
  tiling: { label: "Tiling", trades: [
    { value: "wall_tiling", label: "Wall Tiling" },
    { value: "floor_tiling", label: "Floor Tiling" },
  ]},
  decorating: { label: "Decorating", trades: [
    { value: "painting", label: "Painting" },
    { value: "wallpapering", label: "Wallpapering" },
  ]},
  kitchens: { label: "Kitchen Fitting", trades: [
    { value: "kitchen_fitting", label: "Kitchen Fitting" },
  ]},
  bathrooms: { label: "Bathroom Fitting", trades: [
    { value: "bathroom_fitting", label: "Bathroom Fitting" },
  ]},
  steel: { label: "Steelwork & Structural", trades: [
    { value: "steelwork", label: "Steelwork" },
    { value: "structural_supports", label: "Structural Supports" },
  ]},
  scaffolding: { label: "Scaffolding & Access", trades: [
    { value: "scaffolding", label: "Scaffolding" },
  ]},
  waste: { label: "Waste Removal & Skips", trades: [
    { value: "waste_removal", label: "Waste Removal" },
  ]},
  making_good: { label: "Making Good & Snagging", trades: [
    { value: "making_good", label: "Making Good" },
    { value: "snagging", label: "Snagging" },
  ]},
  preliminaries: { label: "Preliminaries", trades: [
    { value: "site_setup", label: "Site Setup" },
    { value: "welfare", label: "Welfare" },
    { value: "equipment_hire", label: "Equipment Hire" },
    { value: "legal_fees", label: "Legal Fees" },
    { value: "site_equipment", label: "Site Equipment" },
    { value: "fences_barriers", label: "Fences & Barriers" },
    { value: "preparation_protection", label: "Preparation & Protection" },
  ]},
  other: { label: "Other", trades: [
    { value: "other", label: "Other" },
  ]},
};

export const REGIONS = [
  { value: "london", label: "London", uplift: 25 },
  { value: "south_east", label: "South East", uplift: 15 },
  { value: "south_west", label: "South West", uplift: 5 },
  { value: "midlands", label: "Midlands", uplift: 0 },
  { value: "north_west", label: "North West", uplift: -5 },
  { value: "north_east", label: "North East", uplift: -10 },
  { value: "scotland", label: "Scotland", uplift: -5 },
  { value: "wales", label: "Wales", uplift: -5 },
  { value: "national", label: "National Average", uplift: 0 },
  { value: "custom", label: "Custom", uplift: 0 },
] as const;

export const UNITS = {
  sqm: "m²",
  linear_m: "Linear m",
  each: "Each",
  day: "Day",
  hour: "Hour",
  item: "Item",
  allow: "Allowance",
  nr: "Nr",
  // Imperial units — used by US market. Kept on the global UNITS map so that
  // any unit value stored on a line item resolves to a label in the editor
  // (avoids blank-looking <Select> when value is e.g. "sq ft"). Use the
  // getUnits(market) helper below to filter the dropdown options per market.
  "sq ft": "sq ft",
  "lf": "lf",
  "cu ft": "cu ft",
  "cu yd": "cu yd",
  "wk": "wk",
} as const;

// Market-scoped subset of UNITS for dropdown population. UK / AU / CA keep
// metric units only. US shows imperial. The full UNITS map remains the
// resolver for display labels so legacy items still render their unit.
export const UNITS_METRIC: Record<string, string> = {
  sqm: "m²",
  linear_m: "Linear m",
  each: "Each",
  day: "Day",
  hour: "Hour",
  item: "Item",
  allow: "Allowance",
  nr: "Nr",
};

export const UNITS_US: Record<string, string> = {
  "sq ft": "sq ft",
  "lf": "lf",
  "cu ft": "cu ft",
  "cu yd": "cu yd",
  each: "Each",
  item: "Item",
  day: "Day",
  hour: "Hour",
  wk: "wk",
  allow: "Allowance",
};

export function getUnits(market?: string | null): Record<string, string> {
  return market === "us" ? UNITS_US : UNITS_METRIC;
}

export const QUOTE_SECTIONS = [
  { value: "SITE WORK", label: "Site Work" },
  { value: "SUBSTRUCTURE", label: "Substructure" },
  { value: "STRUCTURE", label: "Structure" },
  { value: "SERVICES", label: "Services" },
  { value: "FINISHES", label: "Finishes" },
  { value: "OTHER COSTS", label: "Other Costs" },
  { value: "MAIN WORKS", label: "Main Works" },
] as const;

export const DEFAULT_TERMS_CONDITIONS = `1. Existing Condition
• Quotation assumes the existing surfaces, structure, and services affected by the works are sound and suitable for the proposed works.
• Should hidden defects, damage, or unexpected construction be uncovered once works start, additional works will be priced as variations.

2. Movement & Cracking
• Where structural alterations are included, hairline cracking, minor movement or cosmetic damage may occur due to the age and construction of the property.
• We will take reasonable care; however, we cannot be held responsible for such cosmetic cracking or any pre-existing issues that become exposed once works start.

3. Finishes & Exclusions
• Only the works listed in the Scope of Works are included; no additional finishes, decorating, or reinstatement unless specified.
• Making-good is limited to areas directly affected by the described works.

4. Services
• Client must ensure safe isolation of electrical, plumbing, gas, data or other services in the working area.
• We will not work around live services and will not be liable for hidden/encased services that obstruct the works.

5. Access & Working Conditions
• Price assumes standard access to the work area without the need for scaffolding or external lifting equipment unless specified.
• Working area must be cleared before works commence.

6. Variations
• Any change to the design, site conditions, or client instructions will be priced and agreed before further work proceeds.
• Variations may alter programme and final cost.

7. Programme & Working Hours
• Works are carried out during normal weekday working hours unless agreed otherwise.
• Start date subject to material availability and safe working conditions.`;

// ================== AUSTRALIAN MARKET CONSTANTS ==================
// Kept fully separate from UK constants — UK code path is unchanged.

export const PROJECT_TYPES_AU = [
  { value: "extension", label: "Extension" },
  { value: "loft_conversion", label: "Attic Conversion" },
  { value: "wall_opening", label: "Wall Opening / UB (Universal Beam)" },
  { value: "garage_conversion", label: "Garage Conversion" },
  { value: "kitchen_refurb", label: "Kitchen Renovation" },
  { value: "bathroom_refurb", label: "Bathroom Renovation" },
  { value: "house_refurb", label: "House Renovation" },
  { value: "chimney_removal", label: "Chimney Removal" },
  { value: "full_refurb", label: "Full Renovation" },
  { value: "new_build", label: "New Build" },
  { value: "knockdown_rebuild", label: "Knockdown Rebuild" },
  { value: "granny_flat", label: "Granny Flat / Secondary Dwelling" },
  { value: "deck_pergola", label: "Deck / Pergola / Verandah" },
  { value: "carport", label: "Carport" },
  { value: "roofing", label: "Roofing (incl. Colorbond)" },
  { value: "landscaping", label: "Landscaping" },
  { value: "electrics", label: "Electrical Work" },
  { value: "plumbing", label: "Plumbing Work" },
  { value: "windows_doors", label: "Windows & Doors" },
  { value: "flooring", label: "Flooring" },
  { value: "decorating", label: "Painting & Decorating" },
  { value: "driveway", label: "Driveway / Paving" },
  { value: "fencing", label: "Fencing / Gates" },
  { value: "termite_treatment", label: "Termite Treatment / Barrier" },
  { value: "other", label: "Other" },
] as const;

export const COST_CATEGORIES_AU = [
  { value: "demolition", label: "Demolition & Strip-out" },
  { value: "groundworks", label: "Groundworks & Drainage" },
  { value: "foundations", label: "Foundations & Concrete (Slab)" },
  { value: "brickwork", label: "Brickwork & Blockwork" },
  { value: "carpentry", label: "Carpentry & Framing" },
  { value: "roofing", label: "Roofing (Colorbond / Tile)" },
  { value: "windows_doors", label: "Windows & External Doors" },
  { value: "internal_walls", label: "Internal Walls & Partitions" },
  { value: "plastering", label: "Plastering & Gyprock" },
  { value: "insulation", label: "Insulation & Sarking" },
  { value: "damp_proofing", label: "Waterproofing & Termite Barrier" },
  { value: "electrics", label: "Electrical" },
  { value: "plumbing", label: "Plumbing & Hot Water" },
  { value: "gas_works", label: "Gas Works" },
  { value: "ventilation", label: "Ventilation" },
  { value: "flooring", label: "Flooring" },
  { value: "tiling", label: "Tiling" },
  { value: "decorating", label: "Painting" },
  { value: "kitchens", label: "Kitchen Fit-out" },
  { value: "bathrooms", label: "Bathroom Fit-out" },
  { value: "steel", label: "Steelwork & Structural" },
  { value: "cladding", label: "Cladding (Colorbond / Weatherboard / FC)" },
  { value: "fire_protection", label: "Fire Protection" },
  { value: "external_works", label: "External Works & Landscaping" },
  { value: "staircases", label: "Staircases & Balustrades" },
  { value: "scaffolding", label: "Scaffolding & Access" },
  { value: "waste", label: "Waste Removal & Skip Bins" },
  { value: "making_good", label: "Making Good & Defects" },
  { value: "preliminaries", label: "Preliminaries" },
  { value: "other", label: "Other" },
] as const;

export const COST_TRADES_AU: Record<string, { label: string; trades: { value: string; label: string }[] }> = {
  demolition: { label: "Demolition & Strip-out", trades: [
    { value: "strip_out", label: "Strip-out" },
    { value: "structural_demolition", label: "Structural Demolition" },
  ]},
  groundworks: { label: "Groundworks & Drainage", trades: [
    { value: "excavation", label: "Excavation" },
    { value: "drainage", label: "Stormwater & Sewer Drainage" },
  ]},
  foundations: { label: "Foundations & Concrete", trades: [
    { value: "foundations", label: "Footings" },
    { value: "concrete", label: "Concrete Slab Work" },
    { value: "wall_formwork", label: "Wall Formwork" },
  ]},
  brickwork: { label: "Brickwork & Blockwork", trades: [
    { value: "brickwork", label: "Brick Veneer / Brickwork" },
    { value: "blockwork", label: "Blockwork" },
  ]},
  carpentry: { label: "Carpentry & Framing", trades: [
    { value: "first_fix_carpentry", label: "Rough-in Carpentry" },
    { value: "second_fix_carpentry", label: "Fit-off Carpentry" },
    { value: "roof_carpentry", label: "Roof Framing" },
  ]},
  roofing: { label: "Roofing", trades: [
    { value: "roof_structure", label: "Roof Structure" },
    { value: "roof_coverings", label: "Roof Coverings (Colorbond / Tile)" },
    { value: "leadwork", label: "Flashings" },
    { value: "gutters_downpipes", label: "Gutters & Downpipes" },
  ]},
  windows_doors: { label: "Windows & External Doors", trades: [
    { value: "windows", label: "Windows" },
    { value: "external_doors", label: "External Doors" },
  ]},
  internal_walls: { label: "Internal Walls & Partitions", trades: [
    { value: "stud_walls", label: "Stud Walls" },
    { value: "block_partitions", label: "Block Partitions" },
    { value: "plasterboard", label: "Gyprock / Plasterboard" },
  ]},
  plastering: { label: "Plastering & Gyprock", trades: [
    { value: "plastering_wet", label: "Set Coat / Wet Plaster" },
    { value: "drylining", label: "Direct Stick / Drylining" },
  ]},
  insulation: { label: "Insulation & Sarking", trades: [
    { value: "insulation", label: "Bulk Insulation Batts / Sarking" },
  ]},
  damp_proofing: { label: "Waterproofing & Termite Barrier", trades: [
    { value: "damp_proofing", label: "Damp-proof Course / Termite Barrier" },
    { value: "tanking", label: "Tanking & Waterproofing" },
  ]},
  electrics: { label: "Electrical", trades: [
    { value: "first_fix_electrics", label: "Electrical Rough-in" },
    { value: "second_fix_electrics", label: "Electrical Fit-off" },
    { value: "testing_certification", label: "Testing & Compliance Certificate" },
  ]},
  plumbing: { label: "Plumbing & Hot Water", trades: [
    { value: "first_fix_plumbing", label: "Plumbing Rough-in" },
    { value: "second_fix_plumbing", label: "Plumbing Fit-off" },
    { value: "heating", label: "Hot Water System" },
    { value: "underfloor_heating", label: "Hydronic / Underfloor Heating" },
  ]},
  ventilation: { label: "Ventilation", trades: [
    { value: "ventilation", label: "Ventilation" },
  ]},
  flooring: { label: "Flooring", trades: [
    { value: "screeding", label: "Concrete Topping / Screed" },
    { value: "floor_finishes", label: "Floor Finishes" },
  ]},
  tiling: { label: "Tiling", trades: [
    { value: "wall_tiling", label: "Wall Tiling" },
    { value: "floor_tiling", label: "Floor Tiling" },
  ]},
  decorating: { label: "Painting", trades: [
    { value: "painting", label: "Painting" },
    { value: "wallpapering", label: "Wallpapering" },
  ]},
  kitchens: { label: "Kitchen Fit-out", trades: [
    { value: "kitchen_fitting", label: "Kitchen Fit-out" },
  ]},
  bathrooms: { label: "Bathroom Fit-out", trades: [
    { value: "bathroom_fitting", label: "Bathroom Fit-out" },
  ]},
  steel: { label: "Steelwork & Structural", trades: [
    { value: "steelwork", label: "Steelwork (UB / UC / SHS)" },
    { value: "structural_supports", label: "Structural Supports" },
  ]},
  scaffolding: { label: "Scaffolding & Access", trades: [
    { value: "scaffolding", label: "Scaffolding" },
  ]},
  waste: { label: "Waste Removal & Skip Bins", trades: [
    { value: "waste_removal", label: "Waste Removal / Skip Bin" },
  ]},
  making_good: { label: "Making Good & Defects", trades: [
    { value: "making_good", label: "Making Good" },
    { value: "snagging", label: "Defects Rectification" },
  ]},
  preliminaries: { label: "Preliminaries", trades: [
    { value: "site_setup", label: "Site Establishment" },
    { value: "welfare", label: "Site Amenities" },
    { value: "equipment_hire", label: "Plant & Equipment Hire" },
    { value: "legal_fees", label: "Council Fees / DA / CC" },
    { value: "site_equipment", label: "Site Equipment" },
    { value: "fences_barriers", label: "Site Fencing & Hoarding" },
    { value: "preparation_protection", label: "Preparation & Protection" },
  ]},
  gas_works: { label: "Gas Works", trades: [
    { value: "gas_install", label: "Gas Line Install" },
    { value: "gas_appliance", label: "Gas Appliance Connect" },
    { value: "gas_certify", label: "Gas Compliance Certificate" },
  ]},
  cladding: { label: "Cladding", trades: [
    { value: "colorbond_cladding", label: "Colorbond / Metal Cladding" },
    { value: "weatherboard", label: "Weatherboard / Linea" },
    { value: "fc_sheet", label: "Fibre Cement Sheet" },
    { value: "render_cladding", label: "Render / Acrylic Render" },
  ]},
  fire_protection: { label: "Fire Protection", trades: [
    { value: "passive_fire", label: "Passive Fire Protection" },
    { value: "fire_doors", label: "Fire-rated Doors" },
    { value: "smoke_alarms", label: "Smoke & Heat Alarms" },
    { value: "fire_sprinkler", label: "Fire Sprinkler / Hydrant" },
  ]},
  external_works: { label: "External Works & Landscaping", trades: [
    { value: "paving", label: "Paving & Driveways" },
    { value: "fencing", label: "Fencing & Gates" },
    { value: "decking", label: "Decking" },
    { value: "landscaping", label: "Landscaping & Turf" },
    { value: "retaining_walls", label: "Retaining Walls" },
  ]},
  staircases: { label: "Staircases & Balustrades", trades: [
    { value: "timber_stairs", label: "Timber Stairs" },
    { value: "balustrade", label: "Balustrades & Handrails" },
  ]},
  other: { label: "Other", trades: [
    { value: "termite_management", label: "Termite Management (AS 3660)" },
    { value: "bushfire", label: "Bushfire Compliance (AS 3959)" },
    { value: "solar", label: "Solar PV & Battery" },
    { value: "rainwater", label: "Rainwater Tanks" },
    { value: "pool_safety", label: "Pool Safety & Fencing" },
    { value: "asbestos", label: "Asbestos Removal" },
    { value: "other", label: "Other" },
  ]},
};

export const REGIONS_AU = [
  { value: "national", label: "National (Average)", uplift: 0 },
  { value: "nsw", label: "New South Wales", uplift: 15 },
  { value: "vic", label: "Victoria", uplift: 10 },
  { value: "qld", label: "Queensland", uplift: 5 },
  { value: "wa", label: "Western Australia", uplift: 8 },
  { value: "sa", label: "South Australia", uplift: 0 },
  { value: "tas", label: "Tasmania", uplift: -5 },
  { value: "act", label: "Australian Capital Territory", uplift: 12 },
  { value: "nt", label: "Northern Territory", uplift: 10 },
] as const;

// ─── US category labels ──────────────────────────────────────────────────────
// Mirrors COST_CATEGORIES (UK) but uses US construction English. The category
// `value` keys are identical to UK so existing items stored against UK keys
// (e.g. "damp_proofing", "plastering", "bathrooms") continue to resolve — only
// the display LABEL changes for US users. Same approach as COST_CATEGORIES_AU.
export const COST_CATEGORIES_US = [
  { value: "demolition", label: "Demolition & Tear-Out" },
  { value: "groundworks", label: "Sitework & Drainage" },
  { value: "foundations", label: "Foundations & Concrete" },
  { value: "brickwork", label: "Masonry" },
  { value: "carpentry", label: "Carpentry & Millwork" },
  { value: "roofing", label: "Roofing" },
  { value: "windows_doors", label: "Windows & Exterior Doors" },
  { value: "internal_walls", label: "Interior Walls & Partitions" },
  { value: "plastering", label: "Drywall" },
  { value: "insulation", label: "Insulation & Air Sealing" },
  { value: "damp_proofing", label: "Waterproofing" },
  { value: "electrics", label: "Electrical" },
  { value: "plumbing", label: "Plumbing & Mechanical" },
  { value: "ventilation", label: "Ventilation" },
  { value: "flooring", label: "Flooring" },
  { value: "tiling", label: "Tile" },
  { value: "decorating", label: "Painting" },
  { value: "kitchens", label: "Kitchen Installation" },
  { value: "bathrooms", label: "Bathroom Fixtures" },
  { value: "steel", label: "Structural Steel" },
  { value: "scaffolding", label: "Scaffolding & Access" },
  { value: "waste", label: "Waste Removal & Dumpsters" },
  { value: "making_good", label: "Punch List & Touch-Ups" },
  { value: "preliminaries", label: "General Conditions" },
  { value: "other", label: "Other" },
] as const;

export const COST_TRADES_US: Record<string, { label: string; trades: { value: string; label: string }[] }> = {
  demolition: { label: "Demolition & Tear-Out", trades: [
    { value: "strip_out", label: "Tear-Out" },
    { value: "structural_demolition", label: "Structural Demolition" },
  ]},
  groundworks: { label: "Sitework & Drainage", trades: [
    { value: "excavation", label: "Excavation" },
    { value: "drainage", label: "Site Drainage" },
  ]},
  foundations: { label: "Foundations & Concrete", trades: [
    { value: "foundations", label: "Foundations" },
    { value: "concrete", label: "Concrete Work" },
    { value: "wall_formwork", label: "Wall Formwork" },
  ]},
  brickwork: { label: "Masonry", trades: [
    { value: "brickwork", label: "Brick" },
    { value: "blockwork", label: "Concrete Block (CMU)" },
  ]},
  carpentry: { label: "Carpentry & Millwork", trades: [
    { value: "first_fix_carpentry", label: "Rough Carpentry" },
    { value: "second_fix_carpentry", label: "Finish Carpentry" },
    { value: "roof_carpentry", label: "Roof Framing" },
  ]},
  roofing: { label: "Roofing", trades: [
    { value: "roof_structure", label: "Roof Structure" },
    { value: "roof_coverings", label: "Roof Coverings (Asphalt Shingle / Metal)" },
    { value: "leadwork", label: "Flashings" },
    { value: "gutters_downpipes", label: "Gutters & Downspouts" },
  ]},
  windows_doors: { label: "Windows & Exterior Doors", trades: [
    { value: "windows", label: "Windows" },
    { value: "external_doors", label: "Exterior Doors" },
  ]},
  internal_walls: { label: "Interior Walls & Partitions", trades: [
    { value: "stud_walls", label: "Stud Walls" },
    { value: "block_partitions", label: "CMU Partitions" },
    { value: "plasterboard", label: "Drywall" },
  ]},
  plastering: { label: "Drywall", trades: [
    { value: "plastering_wet", label: "Veneer Plaster / Wet Finish" },
    { value: "drylining", label: "Drywall Hanging" },
  ]},
  insulation: { label: "Insulation & Air Sealing", trades: [
    { value: "insulation", label: "Insulation" },
  ]},
  damp_proofing: { label: "Waterproofing", trades: [
    { value: "damp_proofing", label: "Moisture Barrier" },
    { value: "tanking", label: "Below-Grade Waterproofing" },
  ]},
  electrics: { label: "Electrical", trades: [
    { value: "first_fix_electrics", label: "Electrical Rough-In" },
    { value: "second_fix_electrics", label: "Electrical Trim-Out" },
    { value: "testing_certification", label: "Testing & AHJ Inspection" },
  ]},
  plumbing: { label: "Plumbing & Mechanical", trades: [
    { value: "first_fix_plumbing", label: "Plumbing Rough-In" },
    { value: "second_fix_plumbing", label: "Plumbing Trim-Out" },
    { value: "heating", label: "HVAC / Heating" },
    { value: "underfloor_heating", label: "Radiant Floor Heating" },
  ]},
  ventilation: { label: "Ventilation", trades: [
    { value: "ventilation", label: "Ventilation" },
  ]},
  flooring: { label: "Flooring", trades: [
    { value: "screeding", label: "Floor Leveling / Topping" },
    { value: "floor_finishes", label: "Floor Finishes" },
  ]},
  tiling: { label: "Tile", trades: [
    { value: "wall_tiling", label: "Wall Tile" },
    { value: "floor_tiling", label: "Floor Tile" },
  ]},
  decorating: { label: "Painting", trades: [
    { value: "painting", label: "Painting" },
    { value: "wallpapering", label: "Wallpapering" },
  ]},
  kitchens: { label: "Kitchen Installation", trades: [
    { value: "kitchen_fitting", label: "Kitchen Installation" },
  ]},
  bathrooms: { label: "Bathroom Fixtures", trades: [
    { value: "bathroom_fitting", label: "Bathroom Fixtures" },
  ]},
  steel: { label: "Structural Steel", trades: [
    { value: "steelwork", label: "Steel" },
    { value: "structural_supports", label: "Structural Supports" },
  ]},
  scaffolding: { label: "Scaffolding & Access", trades: [
    { value: "scaffolding", label: "Scaffolding" },
  ]},
  waste: { label: "Waste Removal & Dumpsters", trades: [
    { value: "waste_removal", label: "Waste Removal / Dumpster" },
  ]},
  making_good: { label: "Punch List & Touch-Ups", trades: [
    { value: "making_good", label: "Touch-Ups" },
    { value: "snagging", label: "Punch List" },
  ]},
  preliminaries: { label: "Preliminaries", trades: [
    { value: "site_setup", label: "Site Setup & Mobilization" },
    { value: "welfare", label: "Site Facilities" },
    { value: "equipment_hire", label: "Equipment Rental" },
    { value: "legal_fees", label: "Permit / AHJ Fees" },
    { value: "site_equipment", label: "Site Equipment" },
    { value: "fences_barriers", label: "Site Fencing & Hoarding" },
    { value: "preparation_protection", label: "Preparation & Protection" },
  ]},
  other: { label: "Other", trades: [
    { value: "other", label: "Other" },
  ]},
};

export const DEFAULT_TERMS_CONDITIONS_AU = `1. Existing Structure
• Quotation assumes that the existing wall, brickwork, slab, and supporting structure are sound and suitable for the proposed works.
• Should soft brickwork, voids, weak mortar, termite damage, hidden defects, or unexpected construction be uncovered, additional works will be priced as variations.

2. Movement & Cracking
• Due to the age and construction of the property, hairline cracking, minor movement or cosmetic damage may occur during structural alterations.
• We will take reasonable care; however, we cannot be held responsible for such cosmetic cracking or any pre-existing issues that become exposed once works start.

3. Finishes & Exclusions
• No plastering, set coat, painting or decorating of steelwork is included unless specified.
• No carpentry, boxing-in, fire protection coatings, or reinstatement of finishes unless specified.
• No making-good beyond the immediate structural bearing area.

4. Services
• Client must ensure safe isolation of electrical, plumbing, gas, data or other services in the working area.
• We will not work around live services and will not be liable for hidden/encased services that obstruct the works.

5. Access & Working Conditions
• Price assumes standard internal access without the need for scaffolding or external lifting equipment.
• Working area must be cleared before works commence.

6. Variations
• Any change to the design, site conditions, or client instructions will be priced and agreed before further work proceeds.
• Variations may alter programme and final cost.

7. Programme & Working Hours
• Works are carried out during normal weekday working hours unless agreed otherwise.
• Start date subject to material availability and safe working conditions.

8. GST & Tax
• All amounts are exclusive of GST unless otherwise stated. GST will be added at the rate prevailing on the invoice date.
• A valid tax invoice will be issued in accordance with A New Tax System (Goods and Services Tax) Act 1999.

9. Compliance & Standards
• All works will be carried out in accordance with the National Construction Code (NCC) and applicable Australian Standards (AS).
• Where required, Development Approval (DA), Construction Certificate (CC), and Occupation Certificate (OC) are the responsibility of the client unless agreed otherwise.

10. Australian Consumer Law
• Nothing in this agreement excludes, restricts or modifies any guarantee, condition, warranty or right under the Australian Consumer Law that cannot lawfully be excluded.`;

// ================== CANADA MARKET CONSTANTS ==================
// Mirrors the AU pattern. Kept fully separate from UK and AU constants so
// none of those code paths can be affected.

export const PROJECT_TYPES_CA = [
  { value: "extension", label: "Addition / Extension" },
  { value: "loft_conversion", label: "Attic Conversion" },
  { value: "wall_opening", label: "Wall Opening / Steel Beam" },
  { value: "garage_conversion", label: "Garage Conversion" },
  { value: "kitchen_refurb", label: "Kitchen Renovation" },
  { value: "bathroom_refurb", label: "Bathroom Renovation" },
  { value: "house_refurb", label: "House Renovation" },
  { value: "basement_finish", label: "Basement Finishing" },
  { value: "basement_underpin", label: "Basement Underpinning" },
  { value: "chimney_removal", label: "Chimney Removal" },
  { value: "full_refurb", label: "Full Renovation" },
  { value: "new_build", label: "New Build" },
  { value: "garden_suite", label: "Garden Suite / Laneway House" },
  { value: "secondary_suite", label: "Secondary Suite" },
  { value: "deck_pergola", label: "Deck / Porch / Pergola" },
  { value: "carport", label: "Carport / Garage" },
  { value: "roofing", label: "Roofing (Shingles / Metal)" },
  { value: "siding", label: "Siding / Cladding" },
  { value: "landscaping", label: "Landscaping" },
  { value: "electrics", label: "Electrical Work" },
  { value: "plumbing", label: "Plumbing Work" },
  { value: "windows_doors", label: "Windows & Doors" },
  { value: "flooring", label: "Flooring" },
  { value: "decorating", label: "Painting & Decorating" },
  { value: "driveway", label: "Driveway / Paving" },
  { value: "fencing", label: "Fencing / Gates" },
  { value: "other", label: "Other" },
] as const;

// Canadian provinces and territories. Uplift values are rough labour-cost
// indices vs national average — calibrate against real won quotes.
export const REGIONS_CA = [
  { value: "national", label: "National (Average)", uplift: 0 },
  { value: "on", label: "Ontario", uplift: 8 },
  { value: "qc", label: "Quebec", uplift: 0 },
  { value: "bc", label: "British Columbia", uplift: 12 },
  { value: "ab", label: "Alberta", uplift: 5 },
  { value: "mb", label: "Manitoba", uplift: -5 },
  { value: "sk", label: "Saskatchewan", uplift: -5 },
  { value: "ns", label: "Nova Scotia", uplift: -3 },
  { value: "nb", label: "New Brunswick", uplift: -5 },
  { value: "nl", label: "Newfoundland & Labrador", uplift: -3 },
  { value: "pe", label: "Prince Edward Island", uplift: -5 },
  { value: "yt", label: "Yukon", uplift: 15 },
  { value: "nt", label: "Northwest Territories", uplift: 18 },
  { value: "nu", label: "Nunavut", uplift: 25 },
] as const;

export const DEFAULT_TERMS_CONDITIONS_CA = `1. Existing Structure
• Quotation assumes that the existing wall, foundation, slab, framing and supporting structure are sound and suitable for the proposed works.
• Should rotted lumber, weak mortar, hidden defects, knob-and-tube wiring, asbestos, vermiculite, or unexpected construction be uncovered, additional works will be priced as change orders.

2. Movement & Cracking
• Due to the age and construction of the property, hairline cracking, minor movement or cosmetic damage may occur during structural alterations.
• We will take reasonable care; however, we cannot be held responsible for such cosmetic cracking or any pre-existing issues that become exposed once works start.

3. Finishes & Exclusions
• No drywall finishing, painting or decorating of steelwork is included unless specified.
• No carpentry, boxing-in, fire protection coatings, or reinstatement of finishes unless specified.
• No making-good beyond the immediate structural bearing area.

4. Permits & Inspections
• All works will be carried out in accordance with the National Building Code of Canada (NBC) and the applicable provincial building code.
• Building permit fees, plan-review fees, development charges and municipal inspection fees are excluded unless specified and are payable to the municipality directly by the owner.
• Engineered drawings, structural calculations and geotechnical reports are excluded unless specified.

5. Trades & Certification
• Electrical work will be carried out by a licensed electrician with the appropriate provincial certification (ESA, Technical Safety BC, etc.) and ESA / equivalent inspection certificate provided.
• Plumbing and gas works will be carried out by a licensed plumber / gasfitter and inspected as required by the local authority.

6. Site Conditions
• Asbestos and lead-paint pre-renovation testing is excluded; if hazardous materials are identified, abatement will be priced as a change order under the relevant provincial regulations.
• Owner to provide reasonable access, parking for site vehicles, and use of water and electricity at no cost to the contractor.

7. Variations / Change Orders
• Any change to scope, materials or finishes requested by the owner will be priced and agreed in writing before works proceed.

8. Payment
• Payment terms are as set out in the contract document. Late payment may attract interest at the rate set out in the contract.
• GST and applicable provincial sales tax (PST/HST/QST) will be added at the prevailing rate.

9. Warranty
• Workmanship warranty period as specified in the contract.
• Manufacturer warranties on supplied products will be passed through to the owner.

10. Consumer Protection
• This contract is subject to the consumer protection legislation of the province in which the works are carried out (e.g. Ontario Consumer Protection Act, BC Business Practices and Consumer Protection Act).`;

// ================== UNITED STATES MARKET CONSTANTS ==================
// Mirrors the AU / CA pattern. Kept fully separate so UK / AU / CA code paths
// are untouched. US uses imperial units (sq ft / lf / cy / each) — the unit
// conversion happens at line-item localisation time in `shared/us-localise.ts`.

export const PROJECT_TYPES_US = [
  { value: "extension", label: "Addition / Extension" },
  { value: "loft_conversion", label: "Attic Conversion" },
  { value: "wall_opening", label: "Wall Opening / Steel Beam" },
  { value: "garage_conversion", label: "Garage Conversion" },
  { value: "kitchen_refurb", label: "Kitchen Remodel" },
  { value: "bathroom_refurb", label: "Bathroom Remodel" },
  { value: "house_refurb", label: "Whole-House Remodel" },
  { value: "basement_finish", label: "Basement Finishing" },
  { value: "basement_underpin", label: "Basement Underpinning" },
  { value: "chimney_removal", label: "Chimney Removal" },
  { value: "full_refurb", label: "Full Renovation" },
  { value: "new_build", label: "New Build" },
  { value: "garden_suite", label: "ADU / Backyard Cottage" },
  { value: "secondary_suite", label: "In-Law Suite" },
  { value: "deck_pergola", label: "Deck / Porch / Pergola" },
  { value: "carport", label: "Carport / Garage" },
  { value: "roofing", label: "Roofing (Shingles / Metal)" },
  { value: "siding", label: "Siding / Cladding" },
  { value: "landscaping", label: "Landscaping" },
  { value: "electrics", label: "Electrical Work" },
  { value: "plumbing", label: "Plumbing Work" },
  { value: "windows_doors", label: "Windows & Doors" },
  { value: "flooring", label: "Flooring" },
  { value: "decorating", label: "Painting & Decorating" },
  { value: "driveway", label: "Driveway / Paving" },
  { value: "fencing", label: "Fencing / Gates" },
  { value: "other", label: "Other" },
] as const;

// US regions — major metros and broad regional groupings. Uplift values are
// rough labour-cost indices vs national average; calibrate against real won
// quotes. Shared with the corresponding `regions` block in
// `server/market-config.ts` so UI labels match across the stack.
export const REGIONS_US = [
  { value: "national", label: "National (Average)", uplift: 0 },
  { value: "ca_bay", label: "California — Bay Area", uplift: 35 },
  { value: "ca_la", label: "California — Los Angeles / SoCal", uplift: 25 },
  { value: "ny_metro", label: "New York Metro", uplift: 30 },
  { value: "ma", label: "Massachusetts (Boston)", uplift: 22 },
  { value: "wa_seattle", label: "Washington (Seattle)", uplift: 20 },
  { value: "dc_metro", label: "DC / Maryland / N. Virginia", uplift: 18 },
  { value: "hi", label: "Hawaii", uplift: 35 },
  { value: "ak", label: "Alaska", uplift: 25 },
  { value: "co_denver", label: "Colorado (Denver / Front Range)", uplift: 12 },
  { value: "or_portland", label: "Oregon (Portland)", uplift: 12 },
  { value: "il_chicago", label: "Illinois (Chicago)", uplift: 10 },
  { value: "fl_se", label: "Florida — South / Miami", uplift: 12 },
  { value: "fl_other", label: "Florida — Other", uplift: 5 },
  { value: "tx_metros", label: "Texas (Austin / Dallas / Houston)", uplift: 8 },
  { value: "az_phoenix", label: "Arizona (Phoenix)", uplift: 5 },
  { value: "nv_lv", label: "Nevada (Las Vegas)", uplift: 5 },
  { value: "ga_atlanta", label: "Georgia (Atlanta)", uplift: 3 },
  { value: "nc_metros", label: "North Carolina Metros", uplift: 3 },
  { value: "midwest", label: "Midwest (general)", uplift: -5 },
  { value: "south", label: "South (general)", uplift: -8 },
  { value: "rural", label: "Rural / Tertiary Markets", uplift: -10 },
] as const;

export const DEFAULT_TERMS_CONDITIONS_US = `1. Existing Structure
• Quotation assumes that the existing wall, foundation, slab, framing and supporting structure are sound and suitable for the proposed works.
• Should rotted lumber, weak mortar, hidden defects, knob-and-tube wiring, asbestos, lead paint, or unexpected construction be uncovered, additional works will be priced as change orders.

2. Movement & Cracking
• Due to the age and construction of the property, hairline cracking, minor movement or cosmetic damage may occur during structural alterations.
• We will take reasonable care; however, we cannot be held responsible for such cosmetic cracking or any pre-existing issues that become exposed once works start.

3. Finishes & Exclusions
• No drywall finishing, painting or decorating of steelwork is included unless specified.
• No carpentry, framing-out, fire protection coatings, or restoration of finishes unless specified.
• No making-good beyond the immediate structural bearing area.

4. Permits & Inspections
• All works will be carried out in accordance with the International Residential Code (IRC) or International Building Code (IBC) as adopted by the local Authority Having Jurisdiction (AHJ).
• Building permit fees, plan-review fees, impact fees and municipal inspection fees are excluded unless specified and are payable to the AHJ directly by the owner.
• Engineered drawings, structural calculations, soil reports and Title 24 / energy compliance reports are excluded unless specified.

5. Trades & Certification
• Electrical work will be carried out by a state-licensed electrician in accordance with the National Electrical Code (NFPA 70) and the local AHJ requirements.
• Plumbing and gas works will be carried out by a state-licensed plumber in accordance with the International Plumbing Code (IPC) / Uniform Plumbing Code (UPC) as adopted locally.

6. Site Conditions
• Asbestos and lead-paint pre-renovation testing is excluded; if hazardous materials are identified (homes built pre-1978 are subject to EPA RRP Rule), abatement will be priced as a change order.
• Owner to provide reasonable access, parking for site vehicles, and use of water and electricity at no cost to the contractor.

7. Variations / Change Orders
• Any change to scope, materials or finishes requested by the owner will be priced and agreed in writing before works proceed.

8. Payment
• Payment terms are as set out in the contract document. Late payment may attract interest at the rate set out in the contract.
• Applicable state and local sales tax will be added at the prevailing rate.

9. Warranty
• Workmanship warranty period as specified in the contract.
• Manufacturer warranties on supplied products will be passed through to the owner.

10. Consumer Protection
• This contract is subject to the consumer protection legislation of the state in which the works are carried out (state contractor licensing board rules, mechanics-lien laws, and any applicable home improvement consumer protection acts).`;

// ================== MARKET-AWARE GETTERS ==================
// UK is the default — these getters return UK constants for any unrecognised market.

export function getProjectTypes(market?: string | null) {
  if (market === "au") return PROJECT_TYPES_AU;
  if (market === "ca") return PROJECT_TYPES_CA;
  if (market === "us") return PROJECT_TYPES_US;
  return PROJECT_TYPES;
}

export function getCostCategories(market?: string | null) {
  if (market === "au") return COST_CATEGORIES_AU;
  if (market === "us") return COST_CATEGORIES_US;
  // CA reuses UK cost categories — categorisation aligns closely.
  return COST_CATEGORIES;
}

export function getCostTrades(market?: string | null) {
  if (market === "au") return COST_TRADES_AU;
  if (market === "us") return COST_TRADES_US;
  return COST_TRADES;
}

export function getRegions(market?: string | null) {
  if (market === "au") return REGIONS_AU;
  if (market === "ca") return REGIONS_CA;
  if (market === "us") return REGIONS_US;
  return REGIONS;
}

export function getDefaultTerms(market?: string | null) {
  if (market === "au") return DEFAULT_TERMS_CONDITIONS_AU;
  if (market === "ca") return DEFAULT_TERMS_CONDITIONS_CA;
  if (market === "us") return DEFAULT_TERMS_CONDITIONS_US;
  return DEFAULT_TERMS_CONDITIONS;
}

// CA + US users provide their own combined sales tax rate. CA = GST + PST/HST/QST
// varies by province. US = state + city + special districts varies by jurisdiction.
// `customRate` is the per-user rate stored on the user record.
export function getTaxRate(market?: string | null, customRate?: number | string | null) {
  if (market === "au") return 10;
  if (market === "ca" || market === "us") {
    const n = typeof customRate === "string" ? parseFloat(customRate) : (customRate ?? 0);
    return isFinite(n as number) && (n as number) >= 0 ? (n as number) : 0;
  }
  return 20;
}

export function getTaxLabel(market?: string | null) {
  if (market === "au") return "GST";
  if (market === "ca" || market === "us") return "Sales Tax";
  return "VAT";
}

export function getTaxRegistrationLabel(market?: string | null) {
  if (market === "au") return "ABN";
  if (market === "ca") return "GST/HST Number";
  if (market === "us") return "EIN / TIN";
  return "VAT Number";
}

export function getBankCodeLabel(market?: string | null) {
  if (market === "au") return "BSB";
  if (market === "ca") return "Transit / Institution";
  if (market === "us") return "ABA Routing";
  return "Sort Code";
}

// ================== USER FEEDBACK ==================
export const userFeedback = pgTable("user_feedback", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  type: text("type").notNull().default("idea"),
  page: text("page"),
  message: text("message").notNull(),
  attachmentUrl: text("attachment_url"),
  status: text("status").notNull().default("new"),
  adminReply: text("admin_reply"),
  repliedAt: timestamp("replied_at"),
  replyEmailTrackingToken: varchar("reply_email_tracking_token", { length: 64 }).unique(),
  replyEmailSentAt: timestamp("reply_email_sent_at"),
  replyEmailOpenedAt: timestamp("reply_email_opened_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertUserFeedbackSchema = createInsertSchema(userFeedback).omit({
  id: true,
  replyEmailTrackingToken: true,
  replyEmailSentAt: true,
  replyEmailOpenedAt: true,
  createdAt: true,
});

export type InsertUserFeedback = z.infer<typeof insertUserFeedbackSchema>;
export type UserFeedback = typeof userFeedback.$inferSelect;

// ================== REFERRAL SYSTEM ==================
export const referrals = pgTable("referrals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  referrerUserId: varchar("referrer_user_id").notNull(),
  referredUserId: varchar("referred_user_id").notNull().unique(),
  status: varchar("status").notNull().default("pending"),
  startedAt: timestamp("started_at").defaultNow(),
  activatedAt: timestamp("activated_at"),
  expiresAt: timestamp("expires_at"),
  endedAt: timestamp("ended_at"),
  lastRewardAt: timestamp("last_reward_at"),
}, (table) => [
  index("idx_referrals_referrer").on(table.referrerUserId),
  index("idx_referrals_referred").on(table.referredUserId),
]);

export const insertReferralSchema = createInsertSchema(referrals).omit({
  id: true,
  startedAt: true,
  activatedAt: true,
  expiresAt: true,
  endedAt: true,
  lastRewardAt: true,
});
export type InsertReferral = z.infer<typeof insertReferralSchema>;
export type Referral = typeof referrals.$inferSelect;

export const referralRewards = pgTable("referral_rewards", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  referralId: varchar("referral_id").notNull(),
  referrerUserId: varchar("referrer_user_id").notNull(),
  amount: decimal("amount", { precision: 10, scale: 2 }).notNull().default("9.99"),
  billingCycleDate: timestamp("billing_cycle_date").notNull(),
  status: varchar("status").notNull().default("pending"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_rewards_referral").on(table.referralId),
  index("idx_rewards_referrer").on(table.referrerUserId),
]);

export const insertReferralRewardSchema = createInsertSchema(referralRewards).omit({
  id: true,
  createdAt: true,
});
export type InsertReferralReward = z.infer<typeof insertReferralRewardSchema>;
export type ReferralReward = typeof referralRewards.$inferSelect;

export const referralWallets = pgTable("referral_wallets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().unique(),
  balance: decimal("balance", { precision: 10, scale: 2 }).notNull().default("0.00"),
  lifetimeEarned: decimal("lifetime_earned", { precision: 10, scale: 2 }).notNull().default("0.00"),
  lifetimePaid: decimal("lifetime_paid", { precision: 10, scale: 2 }).notNull().default("0.00"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export type ReferralWallet = typeof referralWallets.$inferSelect;

export const withdrawalRequests = pgTable("withdrawal_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  amount: decimal("amount", { precision: 10, scale: 2 }).notNull(),
  status: varchar("status").notNull().default("pending"),
  accountName: text("account_name").notNull(),
  sortCode: varchar("sort_code").notNull(),
  accountNumber: varchar("account_number").notNull(),
  bankName: text("bank_name"),
  reference: text("reference"),
  paidAt: timestamp("paid_at"),
  rejectedAt: timestamp("rejected_at"),
  rejectionReason: text("rejection_reason"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_withdrawals_user").on(table.userId),
  index("idx_withdrawals_status").on(table.status),
]);

export const insertWithdrawalRequestSchema = createInsertSchema(withdrawalRequests).omit({
  id: true,
  paidAt: true,
  rejectedAt: true,
  rejectionReason: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertWithdrawalRequest = z.infer<typeof insertWithdrawalRequestSchema>;
export type WithdrawalRequest = typeof withdrawalRequests.$inferSelect;

export const savedBankDetails = pgTable("saved_bank_details", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().unique(),
  accountName: text("account_name").notNull(),
  sortCode: varchar("sort_code").notNull(),
  accountNumber: varchar("account_number").notNull(),
  bankName: text("bank_name"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export type SavedBankDetails = typeof savedBankDetails.$inferSelect;

// ================== QUOTE SHARES ==================
export const quoteShares = pgTable("quote_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  quoteId: varchar("quote_id").notNull(),
  sharedByUserId: varchar("shared_by_user_id").notNull(),
  recipientEmail: varchar("recipient_email").notNull(),
  recipientUserId: varchar("recipient_user_id"),
  recipientKind: varchar("recipient_kind").notNull().default("builder"),
  token: varchar("token").notNull().unique(),
  message: text("message"),
  status: varchar("status").notNull().default("pending"),
  clonedQuoteId: varchar("cloned_quote_id"),
  openedAt: timestamp("opened_at"),
  respondedAt: timestamp("responded_at"),
  responseMessage: text("response_message"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  expiresAt: timestamp("expires_at"),
});

export const insertQuoteShareSchema = createInsertSchema(quoteShares).omit({
  id: true,
  createdAt: true,
});

export type QuoteShare = typeof quoteShares.$inferSelect;
export type InsertQuoteShare = z.infer<typeof insertQuoteShareSchema>;

export const REFERRAL_REWARD_AMOUNT = "10.00";

export const TIER_REWARDS_BY_MARKET: Record<string, Record<string, string>> = {
  uk: { starter: "10.00", pro: "15.00", team: "20.00" },
  au: { starter: "20.00", pro: "30.00", team: "40.00" },
  // CA referral rewards in CAD — proportional to CA price tiers.
  ca: { starter: "17.00", pro: "25.00", team: "33.00" },
  // US referral rewards in USD — proportional to US price tiers.
  us: { starter: "13.00", pro: "20.00", team: "27.00" },
};

export function getTierReward(tier: string | null | undefined, market?: string | null): string {
  const m = (market === "au" || market === "ca" || market === "us") ? market : "uk";
  const table = TIER_REWARDS_BY_MARKET[m];
  const t = (tier || "starter").toLowerCase();
  return table[t] ?? table.starter;
}

export function getSignupDiscount(market?: string | null): string {
  return (market === "au" || market === "ca" || market === "us") ? "20.00" : "10.00";
}

export const TIER_AMOUNTS_BY_MARKET: Record<string, Record<string, number>> = {
  uk: { starter: 2900, pro: 5900, team: 9900 },
  au: { starter: 5900, pro: 11900, team: 19900 },
  // CA pricing in CAD cents — calibrated to mirror UK pricing converted at ~1.7 GBP/CAD
  // and rounded to clean retail tiers. Tune once Stripe CA products exist.
  ca: { starter: 4900, pro: 9900, team: 16900 },
  // US pricing in USD cents — calibrated to UK pricing converted at ~1.3 GBP/USD
  // and rounded to clean retail tiers. Tune once Stripe US products exist.
  us: { starter: 3900, pro: 7900, team: 12900 },
};
// New registrations only. Existing accounts retain their persisted trial dates.
export const TRIAL_DURATION_DAYS = 14;

// ================== CHAT (for AI integrations) ==================
export const conversations = pgTable("conversations", {
  id: integer("id").primaryKey().generatedAlwaysAsIdentity(),
  title: text("title").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const messages = pgTable("messages", {
  id: integer("id").primaryKey().generatedAlwaysAsIdentity(),
  conversationId: integer("conversation_id").notNull().references(() => conversations.id, { onDelete: "cascade" }),
  role: text("role").notNull(),
  content: text("content").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

// ================== USER EVENTS (activity tracking) ==================
export const userEvents = pgTable("user_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  eventType: varchar("event_type", { length: 64 }).notNull(),
  page: varchar("page", { length: 128 }),
  eventData: jsonb("event_data"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type UserEvent = typeof userEvents.$inferSelect;

// ================== APP SETTINGS ==================
export const appSettings = pgTable("app_settings", {
  key: varchar("key", { length: 100 }).primaryKey(),
  value: text("value").notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// ================== REFERRAL LINK SLUGS ==================
export const referralLinkClicks = pgTable("referral_link_clicks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  referringUserId: varchar("referring_user_id").notNull(),
  slug: varchar("slug").notNull(),
  clickedAt: timestamp("clicked_at").defaultNow(),
  ipHash: varchar("ip_hash"),
  userAgent: text("user_agent"),
  convertedToSignup: boolean("converted_to_signup").notNull().default(false),
  linkedUserId: varchar("linked_user_id"),
}, (table) => [
  index("idx_rlc_referring_user").on(table.referringUserId),
  index("idx_rlc_slug").on(table.slug),
]);

export type ReferralLinkClick = typeof referralLinkClicks.$inferSelect;

export const slugHistory = pgTable("slug_history", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  oldSlug: varchar("old_slug").notNull(),
  changedAt: timestamp("changed_at").defaultNow(),
  redirectsUntil: timestamp("redirects_until").notNull(),
}, (table) => [
  index("idx_sh_user").on(table.userId),
  index("idx_sh_old_slug").on(table.oldSlug),
]);

export type SlugHistory = typeof slugHistory.$inferSelect;

// ================== EMAIL TRACKING TOKENS ==================
export const emailTrackingTokens = pgTable("email_tracking_tokens", {
  token: varchar("token", { length: 64 }).primaryKey(),
  userId: varchar("user_id").notNull(),
  emailType: varchar("email_type", { length: 64 }).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  openedAt: timestamp("opened_at"),
  clickedAt: timestamp("clicked_at", { withTimezone: true }),
}, (table) => [
  index("idx_ett_user_type").on(table.userId, table.emailType),
]);

export type EmailTrackingToken = typeof emailTrackingTokens.$inferSelect;

// ================== GENERATED QUOTE LOGS ==================
// Snapshot of every AI quote generation request (POST /api/analyze-project),
// persisted regardless of whether the user goes on to save it as a Quote.
// Lets admins audit AI output quality and see what fraction of generations
// convert into saved quotes. Best-effort writes only — failures here must
// never block the analyze-project response.
export const generatedQuoteLogs = pgTable("generated_quote_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  requestId: varchar("request_id"),
  responseSnapshot: jsonb("response_snapshot").$type<Record<string, unknown>>(),
  market: text("market"),
  quoteClass: text("quote_class"), // 'estimate' | 'quote'
  searchMode: text("search_mode"), // 'normal' | 'deep'
  description: text("description"),
  projectTypes: jsonb("project_types").$type<string[]>().default([]),
  customerName: text("customer_name"),
  projectAddress: text("project_address"),
  uploadedFileNames: jsonb("uploaded_file_names").$type<string[]>().default([]),
  uploadedFileKeys: jsonb("uploaded_file_keys").$type<string[]>().default([]),
  // Snapshot of the AI result exactly as returned to the client at generation time.
  projectType: text("project_type"),
  projectSummary: text("project_summary"),
  lineItems: jsonb("line_items").$type<unknown[]>().default([]),
  lineItemCount: integer("line_item_count").default(0),
  assumptions: jsonb("assumptions").$type<string[]>().default([]),
  exclusions: jsonb("exclusions").$type<string[]>().default([]),
  // double precision (not integer) — the AI is instructed to round durations to
  // the nearest 0.5 day (e.g. inspection-only visits), and an integer column
  // rejects that with "invalid input syntax for type integer", silently
  // dropping the entire log row (the insert throws and is swallowed by the
  // caller's non-fatal try/catch), which is what happened in production for a
  // 0.5-day inspection-only generation. See replit.md "Generated Quotes log
  // estimated_days type" for the incident this fixed.
  estimatedDays: doublePrecision("estimated_days"),
  confidenceLevel: text("confidence_level"),
  reviewerNotes: jsonb("reviewer_notes").$type<string[]>().default([]),
  validationFlags: jsonb("validation_flags").$type<unknown[]>().default([]),
  notes: text("notes"),
  // Populated when/if the user saves this generation as a Quote (POST /api/quotes
  // sends back the matching generationLogId). Null means never saved.
  savedQuoteId: varchar("saved_quote_id"),
  savedAt: timestamp("saved_at"),
  // Cached result of the admin "Check" audit (POST /api/admin/generated-quotes/:id/check),
  // so re-opening the record later shows the last result without re-running the AI audit.
  auditResult: jsonb("audit_result").$type<{
    summary: string;
    findings: { category: string; severity: string; detail: string }[];
    report: string;
  } | null>(),
  auditCheckedAt: timestamp("audit_checked_at"),
  // Admin-curated, confirmed findings list — a copy of (a subset/edited version
  // of) auditResult.findings that an admin has explicitly reviewed and signed
  // off on. Kept separate from auditResult so a raw AI re-run never silently
  // overwrites what a human has already confirmed; saving a fresh auditResult
  // (including a re-run) always clears these two columns so a stale PDF can
  // never be produced from unreviewed data.
  confirmedFindings: jsonb("confirmed_findings").$type<
    { category: string; severity: string; detail: string }[] | null
  >(),
  confirmedAt: timestamp("confirmed_at"),
  // Which pipeline was used to generate this quote: "standard" (full pipeline
  // with all post-processing rules) or "core" (3-call, zero post-processing).
  // NULL / absent means "standard" for rows written before this column existed.
  pipelineMode: text("pipeline_mode").default("standard"),
  // Server-measured pricing-request duration. Null on historical records;
  // never infer this from save timestamps or the job's estimated working days.
  generationDurationMs: integer("generation_duration_ms"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_gql_user").on(table.userId),
  index("idx_gql_saved_quote").on(table.savedQuoteId),
  index("idx_gql_created").on(table.createdAt),
  index("idx_gql_request").on(table.userId, table.requestId),
]);

export const insertGeneratedQuoteLogSchema = createInsertSchema(generatedQuoteLogs).omit({
  id: true,
  responseSnapshot: true,
  createdAt: true,
  auditResult: true,
  auditCheckedAt: true,
  confirmedFindings: true,
  confirmedAt: true,
});

export type InsertGeneratedQuoteLog = z.infer<typeof insertGeneratedQuoteLogSchema>;
export type GeneratedQuoteLog = typeof generatedQuoteLogs.$inferSelect;

// ================== SCOPE VOCABULARY GAPS ==================
// "Detect-and-queue" self-learning support (July 2026). During scope
// extraction (POST /api/analyze-project) the AI self-reports construction
// scope phrases it could NOT confidently map onto any canonical item name
// (see "unmappedScopeSignals" in the SCOPE_EXTRACTION_PROMPT output). Each
// signal is persisted here as a pending row for a human admin to review.
// This is deliberately NOT autonomous: the AI only detects and logs gaps —
// a human decides whether a canonical item / prompt rule actually needs to
// change, and that change ships through the normal dev + test + review
// pipeline. Best-effort writes only, mirroring generatedQuoteLogs — a
// logging failure here must never block quote generation.
export const scopeVocabularyGaps = pgTable("scope_vocabulary_gaps", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  generatedQuoteLogId: varchar("generated_quote_log_id"),
  userId: varchar("user_id"),
  market: text("market"),
  phrase: text("phrase").notNull(),
  context: text("context"),
  status: text("status").notNull().default("pending"), // 'pending' | 'approved' | 'dismissed'
  adminNote: text("admin_note"),
  reviewedByAdminId: varchar("reviewed_by_admin_id"),
  reviewedAt: timestamp("reviewed_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_svg_status").on(table.status),
  index("idx_svg_gen_log").on(table.generatedQuoteLogId),
  index("idx_svg_created").on(table.createdAt),
]);

export const insertScopeVocabularyGapSchema = createInsertSchema(scopeVocabularyGaps).omit({
  id: true,
  createdAt: true,
  status: true,
  adminNote: true,
  reviewedByAdminId: true,
  reviewedAt: true,
});

export type InsertScopeVocabularyGap = z.infer<typeof insertScopeVocabularyGapSchema>;
export type ScopeVocabularyGap = typeof scopeVocabularyGaps.$inferSelect;

// ================== ACCOUNT DELETION (safe, multi-step) ==================
// Replaces the old instant admin hard-delete. A request (self-service or
// auto-flagged from inactivity) sits in a review queue; only an explicit
// admin confirmation (re-typed target email) triggers anonymisation. The
// user row itself is NEVER hard-deleted here — PII is scrubbed but
// financial/subscription-adjacent rows (quotes, invoices via Stripe) are
// retained for 6 years. userId is a soft reference (no FK, no cascade) so
// this audit trail survives even if the user row is ever removed by other
// means; a snapshot of identifying fields is stored at request time since
// the live user row will be anonymised by the time this is reviewed.
export const ACCOUNT_DELETION_SOURCES = ["user", "inactivity"] as const;
export type AccountDeletionSource = (typeof ACCOUNT_DELETION_SOURCES)[number];

export const ACCOUNT_DELETION_STATUSES = ["pending", "completed", "cancelled"] as const;
export type AccountDeletionStatus = (typeof ACCOUNT_DELETION_STATUSES)[number];

export const accountDeletionRequests = pgTable("account_deletion_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  // Snapshot at request time, so the queue still shows who/when even after
  // the underlying user row has been anonymised.
  emailSnapshot: text("email_snapshot"),
  nameSnapshot: text("name_snapshot"),
  accountCreatedAt: timestamp("account_created_at"),
  lastSeenAtSnapshot: timestamp("last_seen_at_snapshot"),
  source: text("source").notNull(), // 'user' | 'inactivity'
  status: text("status").notNull().default("pending"), // 'pending' | 'completed' | 'cancelled'
  requestedAt: timestamp("requested_at").defaultNow(),
  resolvedAt: timestamp("resolved_at"),
  resolvedByAdminId: varchar("resolved_by_admin_id"),
  // Free-text admin notes (e.g. why cancelled).
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_adr_user").on(table.userId),
  index("idx_adr_status").on(table.status),
  index("idx_adr_source").on(table.source),
]);

export const insertAccountDeletionRequestSchema = createInsertSchema(accountDeletionRequests).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  resolvedAt: true,
  resolvedByAdminId: true,
});
export type InsertAccountDeletionRequest = typeof accountDeletionRequests.$inferInsert;
export type AccountDeletionRequest = typeof accountDeletionRequests.$inferSelect;

// ================== FEATURE UPDATES (product update emails) ==================
// Admins compose "what's new" announcements here. Each update can be sent to
// a selection of users; sends are tracked per-recipient for open tracking.
// Auto-generated drafts (from the commit-based auto-draft system) carry
// auto_generated=true and a source_commit_sha for deduplication.

export const FEATURE_UPDATE_STATUSES = ["draft", "sent"] as const;
export type FeatureUpdateStatus = (typeof FEATURE_UPDATE_STATUSES)[number];

/** One ordered content block of a feature update: paragraph text or an image URL. */
export type FeatureUpdateSection = { type: "text" | "image"; content: string };

export const featureUpdates = pgTable("feature_updates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  messageType: text("message_type").notNull().default("feature_update"),
  isTemplate: boolean("is_template").notNull().default(false),
  templateName: text("template_name"),
  title: text("title").notNull(),
  body: text("body").notNull().default(""),
  screenshotUrl: text("screenshot_url"),
  // Ordered content blocks (text → image → text → …). When present (non-null),
  // this is authoritative for rendering; body/screenshotUrl remain as a legacy
  // fallback for older rows and the auto-draft generator.
  sections: jsonb("sections").$type<FeatureUpdateSection[]>(),
  // 'draft' = not yet sent; 'sent' = at least one send batch dispatched
  status: text("status").notNull().default("draft"),
  // true when this draft was created automatically from commit messages
  autoGenerated: boolean("auto_generated").notNull().default(false),
  // HEAD commit SHA of the range used to generate this draft (dedup key)
  sourceCommitSha: text("source_commit_sha"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_feature_updates_status").on(table.status),
  index("idx_feature_updates_auto").on(table.autoGenerated),
  index("idx_feature_updates_message_type").on(table.messageType, table.isTemplate),
]);

export type InsertFeatureUpdate = typeof featureUpdates.$inferInsert;
export type FeatureUpdate = typeof featureUpdates.$inferSelect;

export const featureUpdateSends = pgTable("feature_update_sends", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  featureUpdateId: varchar("feature_update_id").notNull(),
  userId: varchar("user_id").notNull(),
  // Snapshot of the recipient email at send time (user may change later)
  recipientEmail: text("recipient_email").notNull(),
  sentAt: timestamp("sent_at").defaultNow(),
  openedAt: timestamp("opened_at"),
  // Nodemailer messageId for reference (null if send failed)
  emailMessageId: text("email_message_id"),
  status: text("status").notNull().default("accepted"),
  error: text("error"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_feature_update_sends_update").on(table.featureUpdateId),
  index("idx_feature_update_sends_user").on(table.userId),
  index("idx_feature_update_sends_message_recipient").on(table.featureUpdateId, table.userId),
]);

// Separate claims retain historical duplicate send rows while enforcing exactly
// one new in-flight/accepted/uncertain send per message and recipient.
export const messageDeliveryClaims = pgTable("message_delivery_claims", {
  featureUpdateId: varchar("feature_update_id").notNull(),
  userId: varchar("user_id").notNull(),
  sendId: varchar("send_id").notNull(),
}, (table) => [
  uniqueIndex("message_delivery_claims_recipient").on(table.featureUpdateId, table.userId),
]);
